import React, { useState, useEffect, useCallback } from 'react';
import { getSafeToken } from '../../utils/tokenHelper';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import * as LucideIcons from 'lucide-react';
import PromptComposerTab from './components/PromptComposerTab';

const builderDebugLog = () => {};
const sanitizeBuildForLogs = (value) => value;

  const TABS = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'onboarding', label: 'Onboarding', icon: 'LogIn' },
    { id: 'clientConfig', label: 'IA Cliente', icon: 'UserCircle' },
    { id: 'scenarios', label: 'Escenarios', icon: 'FileText' },
    { id: 'stateMachine', label: 'State Machine', icon: 'GitBranch' },
    { id: 'coachConfig', label: 'IA Coach', icon: 'GraduationCap' },
    { id: 'moderatorConfig', label: 'IA Moderador', icon: 'ShieldAlert' },
    { id: 'evaluation', label: 'Evaluación', icon: 'Award' },
    { id: 'buildQA', label: 'Build QA', icon: 'ClipboardCheck' },
    { id: 'promptComposer', label: 'Prompt Composer', icon: 'PenTool' },
    { id: 'aiWizard', label: 'Generación asistida (Wizard)', icon: 'Sparkles' }  // 🔒 OCULTO (no se usa)
  ];

// Ticket 3 (P1) UX: el Composer vive como vista única /vertex/builder/composer.
// Wizard (aiWizard) también oculto - no se usa en producción.
const SHOW_PROMPT_COMPOSER_TAB = false;
const SHOW_AI_WIZARD_TAB = false;
const VISIBLE_TABS = TABS.filter((t) => {
  if (!SHOW_PROMPT_COMPOSER_TAB && t.id === 'promptComposer') return false;
  if (!SHOW_AI_WIZARD_TAB && t.id === 'aiWizard') return false;
  return true;
});

function AgentEditor({ mode = 'edit' }) {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();

  const creationModeRaw = new URLSearchParams(location.search).get('mode');
  const creationMode = creationModeRaw === 'composer' || creationModeRaw === 'manual' ? creationModeRaw : null;
  const isNewAgent = mode === 'new';
  const showInitialModeSelector = isNewAgent && !creationMode;
  const isComposerMode = isNewAgent && creationMode === 'composer';
  const isManualMode = !isNewAgent || creationMode === 'manual';

  const setCreationMode = useCallback(
    (nextMode) => {
      const params = new URLSearchParams(location.search);
      if (nextMode === 'composer' || nextMode === 'manual') {
        params.set('mode', nextMode);
      } else {
        params.delete('mode');
      }
      const nextSearch = params.toString();
      navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );
  
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(mode !== 'new');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showArchitectureHelp, setShowArchitectureHelp] = useState(false);

  // Build QA
  const [buildReport, setBuildReport] = useState(null);
  const [buildQaLoading, setBuildQaLoading] = useState(false);
  const [buildQaError, setBuildQaError] = useState(null);

  // Wizard (TCK-P0-AGENT-WIZARD-AUTO-BLUEPRINT)
  const [wizardInputs, setWizardInputs] = useState({
    nombreVisible: '',
    counterpartRole: '',
    userRole: '',
    pedagogicalObjective: '',
    simulationContext: '',
    phaseCount: 2,
    maxTurns: 6
  });
  const [wizardGenLoading, setWizardGenLoading] = useState(false);
  const [wizardGenError, setWizardGenError] = useState(null);
  const [wizardGeneratedAgent, setWizardGeneratedAgent] = useState(null);
  const [wizardQa, setWizardQa] = useState(null);
  const [wizardQaRaw, setWizardQaRaw] = useState(null);
  const [wizardGeneratorMeta, setWizardGeneratorMeta] = useState(null);
  const [wizardGenState, setWizardGenState] = useState('idle');
  const [wizardGenerationReport, setWizardGenerationReport] = useState(null);
  const [wizardPreviewOpen, setWizardPreviewOpen] = useState(false);

  // Wizard Partial Regen (P3-08A/B/C)
  const [partialRegenPatchScope, setPartialRegenPatchScope] = useState(['stateMachine']);
  const [partialRegenLoading, setPartialRegenLoading] = useState(false);
  const [partialRegenError, setPartialRegenError] = useState(null);
  const [partialRegenPreview, setPartialRegenPreview] = useState(null);
  const [partialRegenApplyLoading, setPartialRegenApplyLoading] = useState(false);
  const [partialRegenApplyError, setPartialRegenApplyError] = useState(null);

  const deriveWizardGlobalMaxTurns = (phaseCount) => {
    if (phaseCount === 1) return 4;
    if (phaseCount === 2) return 6;
    if (phaseCount === 3) return 8;
    if (phaseCount === 4) return 10;
    if (phaseCount === 5) return 12;
    return 14;
  };

  const isEmptyValue = (v) => {
    if (v == null) return true;
    if (typeof v === 'string') return v.trim().length === 0;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
  };

  const mergeFillEmpty = (target, source) => {
    if (source == null) return target;
    if (typeof source !== 'object' || Array.isArray(source)) {
      return isEmptyValue(target) ? source : target;
    }
    const next = (target && typeof target === 'object' && !Array.isArray(target)) ? { ...target } : {};
    Object.keys(source).forEach((k) => {
      const srcVal = source[k];
      const tgtVal = next[k];
      if (srcVal == null) return;

      if (typeof srcVal === 'object' && !Array.isArray(srcVal)) {
        next[k] = mergeFillEmpty(tgtVal, srcVal);
        return;
      }

      if (Array.isArray(srcVal)) {
        next[k] = (Array.isArray(tgtVal) && tgtVal.length > 0) ? tgtVal : srcVal;
        return;
      }

      next[k] = isEmptyValue(tgtVal) ? srcVal : tgtVal;
    });
    return next;
  };

  const pickNewAgentBase = () => {
    if (wizardGenState === 'ready' && wizardQa?.pceResult === 'PASS' && wizardGeneratedAgent) return wizardGeneratedAgent;
    return null;
  };

  useEffect(() => {
    // Wizard: si cambia phaseCount, recalcular maxTurns por defecto
    setWizardInputs((prev) => ({
      ...prev,
      maxTurns: deriveWizardGlobalMaxTurns(Number(prev.phaseCount) || 2)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardInputs.phaseCount]);


  
  // Agent data structure
  const [agentData, setAgentData] = useState({
    agentId: '',
    nombreVisible: '',
    descripcion: '',
    avatar: '',
    status: 'draft',
    maxTurns: 30,
    temperature: 0.7,
    maxOutputTokens: 300,
    topP: 0.9,
    model: 'gemini-2.5-flash-lite',
    systemPrompt: '',
    vertexProjectId: '',
    vertexLocation: '',
    // Configuración de Onboarding (nuevo)
    onboarding: {
      title: 'Bienvenido al Simulador',
      message: 'En esta práctica pondrás a prueba tus habilidades ejecutivas con un cliente IA.',
      consent: 'Tus respuestas serán evaluadas para generar métricas de desempeño.'
    },
    // Motor v2.0: Coaching Mode
    coachingMode: 'balanced',
    coachingConfig: {
      maxInterventionsPerSession: 3,
      minTurnsBetweenInterventions: 3,
      criticalErrorsOnly: true,
      methodologiesToMention: ['BATNA', 'ZOPA', 'Criterios objetivos', 'Intereses vs posiciones']
    },
    // Motor V3: Configuración Multi-Agente
    clientConfig: {
      enabled: true,
      provider: 'openai',
      model: 'gpt-4o-mini',
      temperature: 0.7,
      maxTokens: 300,
      roleDefinition: 'Eres un ejecutivo senior que evalúa propuestas comerciales.',
      personalityTraits: [],
      difficultyLevel: 'medium',  // 'low' | 'medium' | 'high'
      behaviorInstructions: 'Mantén un tono profesional pero exigente.'
    },
    coachConfig: {
      enabled: true,
      provider: 'gemini',
      model: 'gemini-2.0-flash-exp',
      temperature: 0.3,
      evaluationFrequency: 2,
      interventionMode: 'balanced',
      evaluationCriteria: [],
      ragEnabled: true,
      methodologies: ['BATNA', 'ZOPA', 'Criterios objetivos', 'Intereses vs posiciones'],
      // Brief Inicial (Narrador) - Motor V3
      briefing: {
        enabled: true,  // Default true (consistente con backend)
        prompt: '',
        ragMode: 'off'
      }
    },
    moderatorConfig: {
      enabled: true,
      model: 'gpt-4o-mini',
      temperature: 0.1,
      evaluationFrequency: 2,
      historyWindow: 10,
      promptBase: `Eres un moderador automático que supervisa el comportamiento del estudiante en una conversación educativa.

TU ÚNICA FUNCIÓN: Detectar comportamiento inapropiado.

DETECTA:
1. Lenguaje ofensivo o vulgar
2. Spam: Mensajes repetitivos sin sentido (ej: "jaja", "ok ok", "...")
   - Analiza TODOS los mensajes USER del historial para encontrar patrones
   - Ejemplos: USER escribe "jaja" 3 veces seguidas, o solo ".", "a", "x"
3. Abandono: Usuario no responde coherentemente después de varios turnos
4. Incoherencias: Respuestas que no tienen relación con el contexto

NO EVALÚES:
- Técnica de negociación
- Calidad pedagógica de las respuestas
- Aplicación de metodologías (BATNA, ZOPA, etc.)
- Si la respuesta es "correcta" o "incorrecta"

NIVEL DE ESTRICTEZ: NORMAL
- Spam: 3+ mensajes similares/vacíos
- Ofensivo: Cualquier lenguaje inapropiado
- Abandono: No responde después de 3+ turnos

ACCIONES:
- "none": Todo OK
- "warn": Advertencia leve
- "pause_session": Pausa (requiere acción del usuario)
- "end_session": Finaliza sesión (violación grave)`,
      customRules: []
    },
    memoryConfig: {
      enabled: true,
      clientMemoryWindow: 10,  // V3: Memoria del Cliente (OpenAI)
      coachMemoryWindow: 6,    // V3: Memoria del Coach (Gemini)
      includeMetadata: true,
      compressionStrategy: 'none'  // Interno, no configurable en Builder V3
    },
    ragConfig: {
      enabled: false,
      primaryCorpus: ''
    },
    stateMachine: {
      enabled: false,
      initialPhase: '',
      phases: []
    },
    scenarioTemplates: [],
    interventionTriggers: [],
    evaluation: {
      enabled: false,
      generalConfig: {
        minimumTurnsForEvaluation: 3,
        includeTranscriptSummary: true,
        includeStrengths: true,
        includeWeaknesses: true,
        includeActionPlan: true
      },
      competencies: [],
      feedbackSettings: {
        tone: 'motivacional',
        detailLevel: 'detallado',
        includeExamples: true,
        language: 'es'
      }
    },
    // Labels personalizables para evaluación
    labels: {
      participantRole: 'PARTICIPANTE',
      clientRole: 'CLIENTE',
      activityType: 'simulación'
    }
  });

  // Access control
  useEffect(() => {
    if (user?.email !== 'davidprudencio@hotmail.com') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load agent data
  useEffect(() => {
    if (mode !== 'new' && agentId) {
      fetchAgentData();
    }
  }, [agentId, mode]);

  const fetchAgentData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Obtener token desde contexto o localStorage como fallback
      const authToken = token || getSafeToken();
      builderDebugLog('[AgentEditor] fetchAgentData token:', authToken);

      if (!authToken) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }

      const headers = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(`${VERTEX_API_BASE}/v4/agents/${agentId}`, { headers });

      builderDebugLog('[AgentEditor] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        builderDebugLog('[AgentEditor] Error response:', errorText);
        setError(`Error HTTP ${response.status}: ${errorText}`);
        setLoading(false);
        return;
      }

      const data = await response.json();
      builderDebugLog('[AgentEditor] Agent data loaded (sanitized):', sanitizeBuildForLogs(data));
      
      if (data.success && data.agent) {
        // Transformar scenarioTemplates del backend al formato del frontend
        const transformedScenarios = (data.agent.scenarioTemplates || []).map(scenario => ({
          escenarioId: scenario.scenarioId,
          nombre: scenario.title || '',
          descripcion: scenario.description || '',
          descripcionContexto: scenario.descripcionContexto || scenario.context || '',
          objetivoPedagogico: scenario.objetivoPedagogico || scenario.pedagogicalGoal || '',
          tags: scenario.tags || [],
          dificultad: scenario.dificultad || (scenario.difficulty === 'beginner' ? 'low' :
                     scenario.difficulty === 'intermediate' ? 'medium' :
                     scenario.difficulty === 'advanced' ? 'high' : 'medium'),
          difficultyReason: scenario.difficultyReason || '',
          // Mantener campos originales por si se necesitan
          industria: scenario.industria || scenario.industry || 'General',
          counterpart: scenario.counterpart,
          variables: scenario.variables
        }));

        // Asegurar estructura completa con valores por defecto
        const loadedAgent = {
          ...data.agent,
          // Asegurar coachConfig con valores por defecto
          coachConfig: {
            enabled: data.agent.coachConfig?.enabled !== undefined ? data.agent.coachConfig.enabled : true,
            mode: data.agent.coachConfig?.mode || 'on-demand',
            interventionMode: data.agent.coachConfig?.interventionMode || 'balanced',
            promptBase: data.agent.coachConfig?.promptBase || '',
            temperature: data.agent.coachConfig?.temperature !== undefined ? data.agent.coachConfig.temperature : 0.3,
            maxTokens: data.agent.coachConfig?.maxTokens || 800,
            evaluationTokenMultiplier: data.agent.coachConfig?.evaluationTokenMultiplier || 2.5,
            evaluationFrequency: data.agent.coachConfig?.evaluationFrequency || 2,
            evaluationCriteria: data.agent.coachConfig?.evaluationCriteria || [],
            ragEnabled: data.agent.coachConfig?.ragEnabled !== false,
            corpusId: data.agent.coachConfig?.corpusId || '',
            vertexProjectId: data.agent.coachConfig?.vertexProjectId || '',
            vertexLocation: data.agent.coachConfig?.vertexLocation || '',
            methodologies: data.agent.coachConfig?.methodologies || [],
            maxHelpRequests: data.agent.coachConfig?.maxHelpRequests || 5,
            helpCooldown: data.agent.coachConfig?.helpCooldown || 1,
            // Brief Inicial (Narrador) - Motor V3
            briefing: {
              enabled: data.agent.coachConfig?.briefing?.enabled ?? true,  // Default true (igual que backend)
              prompt: data.agent.coachConfig?.briefing?.prompt || '',
              ragMode: data.agent.coachConfig?.briefing?.ragMode || 'off'
            }
          },
          // Mapear evaluationCriteria del backend a evaluation del frontend (FASE 1)
          evaluation: data.agent.evaluationCriteria ? {
            enabled: data.agent.evaluationCriteria.enabled || false,
            generalConfig: data.agent.evaluationCriteria.generalConfig || {
              minimumTurnsForEvaluation: 3,
              includeTranscriptSummary: true,
              includeStrengths: true,
              includeWeaknesses: true,
              includeActionPlan: true
            },
            competencies: (data.agent.evaluationCriteria.competencies || []).map(comp => ({
              competencyId: comp.competencyId,
              displayName: comp.displayName || '',
              description: comp.description || '',
              weight: comp.weight || 20,
              criteria: (comp.criteria || []).map(crit => ({
                criterionId: crit.criterionId,
                description: crit.description,
                maxScore: crit.maxScore || 100
              }))
            })),
            feedbackSettings: data.agent.evaluationCriteria.feedbackSettings || {
              tone: 'motivacional',
              detailLevel: 'detallado',
              includeExamples: true,
              language: 'es'
            }
          } : {
            enabled: false,
            generalConfig: {
              minimumTurnsForEvaluation: 3,
              includeTranscriptSummary: true,
              includeStrengths: true,
              includeWeaknesses: true,
              includeActionPlan: true
            },
            competencies: [],
            feedbackSettings: {
              tone: 'motivacional',
              detailLevel: 'detallado',
              includeExamples: true,
              language: 'es'
            }
          },
          // Mapear labels del backend (merge con defaults para no perder campos)
          labels: {
            participantRole: 'PARTICIPANTE',
            clientRole: 'CLIENTE',
            activityType: 'simulación',
            ...(data.agent.labels || {})
          },
          interventionTriggers: data.agent.interventionTriggers || [],
          scenarioTemplates: transformedScenarios
        };
        
        builderDebugLog('LoadedAgent después de transformación (sanitized):', {
          agentId: loadedAgent.agentId,
          status: loadedAgent.status
        });
        
        setAgentData(loadedAgent);

        if (data.agent.lastBuildReport) {
          setBuildReport(data.agent.lastBuildReport);
        }
      } else {
        setError('No se pudo cargar el agente');
      }
    } catch (err) {
      console.error('Error loading agent:', err);
      setError('Error al cargar los datos del agente');
    } finally {
      setLoading(false);
    }
  };

  // Función para publicar/despublicar agente
  const handlePublish = async () => {
    // Usar agentData.agentId en lugar de agentId de URL params
    const realAgentId = agentData.agentId || agentId;
    if (!realAgentId) {
      setError('Debe guardar el agente antes de publicarlo');
      return;
    }

    // Build QA P0 bloquea publicación
    if ((buildReport?.summary?.counts?.P0 || 0) > 0) {
      setError('Build bloqueado por P0. Corregir antes de publicar.');
      return;
    }

    builderDebugLog('Publicando agente (sanitized):', {
      urlAgentId: agentId,
      agentDataAgentId: agentData.agentId,
      realAgentId,
      status: agentData.status
    });

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const authToken = token || getSafeToken();
      if (!authToken) {
        throw new Error('No se encontró token de autenticación');
      }

      const newStatus = agentData.status === 'published' ? 'draft' : 'published';
      const endpoint = `${VERTEX_API_BASE}/v4/agents/${realAgentId}`;
      
      builderDebugLog('Endpoint:', endpoint, 'Nuevo estado:', newStatus);
      
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ status: newStatus, isActive: newStatus === 'published' })
      });

      builderDebugLog('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      builderDebugLog('Response data (sanitized):', sanitizeBuildForLogs(data));

      if (data.success) {
        setAgentData(prev => ({ ...prev, status: newStatus, isActive: newStatus === 'published' }));
        setSuccessMessage(newStatus === 'published' ? 
          'Agente publicado - Ahora disponible para entrenamientos' : 
          'Agente despublicado - Guardado como borrador'
        );
      } else {
        throw new Error(data.error || 'Error al cambiar estado del agente');
      }
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ✅ FIX TCK-P0-WIZARD-NEXTPHASEID-HARDCODED: Auto-wire nextPhaseId antes de guardar
  const autoWirePhases = (phases) => {
    if (!Array.isArray(phases) || phases.length === 0) return phases;
    
    return phases.map((phase, idx) => ({
      ...phase,
      nextPhaseId: idx < phases.length - 1 ? phases[idx + 1].phaseId : null
    }));
  };

  const handleSave = async () => {
    try {
      // P0 Build QA bloquea Guardar (contrato UX P0)
      const buildP0 = buildReport?.summary?.counts?.P0 || 0;
      if (buildReport && buildP0 > 0) {
        setError('Build bloqueado por P0. Corregir antes de guardar.');
        return;
      }

      // TCK-P0-AGENT-GEN-UX-CONTRACT: En "Nuevo Agente" el guardado depende de generación + QA PASS
      if (mode === 'new') {
        const base = pickNewAgentBase();
        if (!base) {
          setError('Debe generar una configuración válida (Build QA PASS) antes de guardar.');
          return;
        }
      }

      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const endpoint = mode === 'new' 
        ? `${VERTEX_API_BASE}/v4/agents`
        : `${VERTEX_API_BASE}/v4/agents/${agentId}`;
      
      const method = mode === 'new' ? 'POST' : 'PUT';

      const authToken = token || getSafeToken();
      if (!authToken) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        setSaving(false);
        return;
      }

      // Base payload: en modo new, se persiste el agente generado por Wizard
      const baseAgent = mode === 'new'
        ? pickNewAgentBase()
        : agentData;

      if (!baseAgent) {
        setError('Debe generar una configuración válida (Build QA PASS) antes de guardar.');
        setSaving(false);
        return;
      }

      // Transformar scenarioTemplates SOLO si vienen en formato frontend (escenarioId/nombre/...)
      const scenarios = Array.isArray(baseAgent.scenarioTemplates) ? baseAgent.scenarioTemplates : [];
      const isFrontendScenarioShape = scenarios.length > 0 && (scenarios[0]?.escenarioId || scenarios[0]?.nombre || scenarios[0]?.descripcion);

      const transformedScenarios = isFrontendScenarioShape
        ? scenarios.map(scenario => ({
            scenarioId: scenario.escenarioId || scenario.scenarioId,
            title: scenario.nombre || scenario.title || '',
            description: scenario.descripcion || scenario.description || '',
            descripcionContexto: scenario.descripcionContexto || scenario.context || '',
            objetivoPedagogico: scenario.objetivoPedagogico || scenario.pedagogicalGoal || '',
            pedagogicalGoal: scenario.objetivoPedagogico || scenario.pedagogicalGoal || '',
            tags: scenario.tags || [],
            dificultad: scenario.dificultad || 'medium',
            difficultyReason: scenario.difficultyReason || '',
            difficulty: scenario.dificultad === 'low' ? 'beginner' : 
                       scenario.dificultad === 'medium' ? 'intermediate' : 
                       scenario.dificultad === 'high' ? 'advanced' : 'intermediate',
            industria: scenario.industria || scenario.industry || 'General',
            industry: scenario.industria || scenario.industry || 'General',
            context: scenario.descripcionContexto || scenario.context || '',
            counterpart: scenario.counterpart || {
              name: 'Contraparte',
              role: 'Decisor',
              personality: 'analytical'
            },
            variables: scenario.variables || {}
          }))
        : scenarios;

      // ✅ FIX TCK-P0-WIZARD-NEXTPHASEID-HARDCODED: Auto-wire nextPhaseId antes de construir payload
      const stateMachineWithAutoWire = baseAgent.stateMachine?.phases 
        ? {
            ...baseAgent.stateMachine,
            phases: autoWirePhases(baseAgent.stateMachine.phases)
          }
        : baseAgent.stateMachine;

      if (stateMachineWithAutoWire?.phases) {
        console.log('[AgentEditor] ✅ Auto-wire aplicado a stateMachine.phases:', 
          stateMachineWithAutoWire.phases.map(p => `${p.phaseId} → ${p.nextPhaseId || 'null'}`).join(', ')
        );
      }

      // Preparar payload con transformaciones
      const payload = {
        ...baseAgent,
        scenarioTemplates: transformedScenarios,
        stateMachine: stateMachineWithAutoWire,
        // Guardar NO publica. Publicar es acción explícita.
        status: baseAgent.status || 'draft',
        isActive: (baseAgent.status || 'draft') === 'published',
        // ASEGURAR que coachConfig se envíe completo
        coachConfig: {
          ...baseAgent.coachConfig,
          maxTokens: baseAgent.coachConfig?.maxTokens || 500,
          evaluationTokenMultiplier: baseAgent.coachConfig?.evaluationTokenMultiplier || 2.5,
          // CRÍTICO: Asegurar que briefing se envíe siempre (incluso si está vacío)
          briefing: {
            enabled: baseAgent.coachConfig?.briefing?.enabled ?? true,
            prompt: baseAgent.coachConfig?.briefing?.prompt || '',
            ragMode: baseAgent.coachConfig?.briefing?.ragMode || 'off',
            model: baseAgent.coachConfig?.briefing?.model || 'gemini-2.0-flash-exp',
            temperature: baseAgent.coachConfig?.briefing?.temperature || 0.7,
            maxTokens: baseAgent.coachConfig?.briefing?.maxTokens || 800
          }
        },
        // ⚠️ LEGACY: Copiar valores de coachConfig al nivel raíz del agente
        // TODO: Eliminar estos campos después de migrar todas las referencias en vertex_routes.js
        // Actualmente el backend lee de estos campos legacy en ~13 ubicaciones (líneas 694, 695, 767, 768, 1026, 3462, 3469, 3470, 3773, 3774, 4475, 4476)
        // Nuevos desarrollos deben leer directamente de agent.coachConfig.vertexProjectId, agent.coachConfig.vertexLocation, agent.coachConfig.corpusId
        vertexProjectId: agentData.coachConfig?.vertexProjectId || agentData.vertexProjectId || undefined,
        vertexLocation: agentData.coachConfig?.vertexLocation || agentData.vertexLocation || undefined,
        ragCorpusId: agentData.coachConfig?.corpusId || agentData.ragCorpusId || undefined,
        // Filtrar triggers incompletos (sin condition.value o intervention.message)
        interventionTriggers: (agentData.interventionTriggers || []).filter(trigger => 
          trigger.condition?.value && 
          trigger.intervention?.message &&
          trigger.triggerId
        ),
        // Mapear evaluation del frontend a evaluationCriteria del backend (FASE 1)
        evaluationCriteria: agentData.evaluation ? {
          enabled: agentData.evaluation.enabled || false,
          generalConfig: agentData.evaluation.generalConfig || {
            minimumTurnsForEvaluation: 3,
            includeTranscriptSummary: true,
            includeStrengths: true,
            includeWeaknesses: true,
            includeActionPlan: true
          },
          competencies: (agentData.evaluation.competencies || []).map(comp => ({
            competencyId: comp.competencyId,
            displayName: comp.displayName,
            description: comp.description || '',
            weight: comp.weight || 20,
            criteria: (comp.criteria || []).map(crit => ({
              criterionId: crit.criterionId,
              description: crit.description,
              maxScore: crit.maxScore || 100
            }))
          })),
          feedbackSettings: agentData.evaluation.feedbackSettings || {
            tone: 'motivacional',
            detailLevel: 'detallado',
            includeExamples: true,
            language: 'es'
          }
        } : undefined,
        // Labels personalizables para evaluación (merge con defaults)
        labels: {
          participantRole: 'PARTICIPANTE',
          clientRole: 'CLIENTE',
          activityType: 'simulación',
          ...(agentData.labels || {})
        },
        // Eliminar campos del frontend (usamos los del backend)
        evaluation: undefined
      };

      builderDebugLog('Payload a enviar (sanitized):', sanitizeBuildForLogs(payload));

      const headers = { 'Content-Type': 'application/json' };
      headers['Authorization'] = `Bearer ${authToken}`;

      const response = await fetch(endpoint, {
        method,
        headers,
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(mode === 'new' ? 'Agente creado exitosamente' : 'Cambios guardados exitosamente');
        setTimeout(() => {
          if (mode === 'new') {
            navigate(`/vertex/builder/edit/${data.agent.agentId}`);
          }
        }, 1500);
      } else {
        setError(data.message || 'Error al guardar el agente');
      }
    } catch (err) {
      console.error('Error saving agent:', err);
      setError('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setAgentData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Hooks for State Machine (moved to top level)
  const addPhase = useCallback(() => {
    const newPhase = {
      phaseId: '',
      displayName: '',
      minTurns: 1,
      maxTurns: 5,
      objectives: [],
      phasePrompt: '',
      onEnterPrompt: '',
      onExitPrompt: '',
      transitionRules: {
        automatic: true,
        triggers: []
      }
    };
    
    setAgentData(prev => ({
      ...prev,
      stateMachine: {
        ...prev.stateMachine,
        phases: [...prev.stateMachine.phases, newPhase]
      }
    }));
  }, []);

  const removePhase = useCallback((index) => {
    setAgentData(prev => ({
      ...prev,
      stateMachine: {
        ...prev.stateMachine,
        phases: prev.stateMachine.phases.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const updatePhase = useCallback((index, field, value) => {
    setAgentData(prev => ({
      ...prev,
      stateMachine: {
        ...prev.stateMachine,
        phases: prev.stateMachine.phases.map((phase, i) => 
          i === index ? { ...phase, [field]: value } : phase
        )
      }
    }));
  }, []);

  const renderGeneralTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Agent ID
          </label>
          <input
            type="text"
            value={agentData.agentId}
            onChange={(e) => updateField('agentId', e.target.value)}
            disabled={mode === 'edit'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
            placeholder="coach-negociacion"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre Visible
          </label>
          <input
            type="text"
            value={agentData.nombreVisible}
            onChange={(e) => updateField('nombreVisible', e.target.value)}
            disabled={mode === 'view'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Coach de Negociación"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Avatar
          </label>
          
          {/* Preview de la imagen actual */}
          {agentData.avatar && (
            <div className="mb-3 flex items-center gap-3">
              {agentData.avatar.startsWith('/') ? (
                <img 
                  src={agentData.avatar} 
                  alt="Avatar preview" 
                  className="w-16 h-16 rounded-full border-4 border-indigo-100 object-cover shadow-md"
                />
              ) : (
                <div className="text-4xl">{agentData.avatar}</div>
              )}
              <button
                type="button"
                onClick={() => updateField('avatar', '')}
                className="text-sm text-red-600 hover:text-red-700"
                disabled={mode === 'view'}
              >
                Limpiar
              </button>
            </div>
          )}

          {/* Input de texto para ruta o emoji */}
          <div className="space-y-2">
            <input
              type="text"
              value={agentData.avatar}
              onChange={(e) => updateField('avatar', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="🤖"
            />
            <p className="text-xs text-gray-500">
              • Emoji: 👨‍💼 💼 🎯 📊 💡 🏆<br/>
              • Imagen: /avatars/mi-avatar.png<br/>
              • Sube tu imagen a <code className="bg-gray-100 px-1 rounded">public/avatars/</code>
            </p>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descripción
          </label>
          <textarea
            value={agentData.descripcion}
            onChange={(e) => updateField('descripcion', e.target.value)}
            disabled={mode === 'view'}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Entrenador especializado en técnicas de negociación..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estado
          </label>
          <select
            value={agentData.status}
            onChange={(e) => updateField('status', e.target.value)}
            disabled={mode === 'view'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="draft">Borrador</option>
            <option value="published">Publicado</option>
            <option value="archived">Archivado</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Máximo de Turnos
          </label>
          <input
            type="number"
            value={agentData.maxTurns}
            onChange={(e) => updateField('maxTurns', parseInt(e.target.value))}
            disabled={mode === 'view'}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            min="1"
            max="100"
          />
        </div>

        {/* ADVERTENCIA: Migración V3 */}
        <div className="md:col-span-2">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <LucideIcons.AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  ⚡ Motor V3: Configuración Separada por Agente
                </h4>
                <p className="text-xs text-yellow-800">
                  En Motor V3, cada agente IA tiene su propia configuración independiente:
                </p>
                <ul className="text-xs text-yellow-800 mt-2 space-y-1 ml-4 list-disc">
                  <li><strong>IA Cliente:</strong> Rol, personalidad, dificultad, temperature, maxTokens, modelo (OpenAI)</li>
                  <li><strong>IA Coach:</strong> Prompt base, modo intervención, frecuencia, temperature, RAG (Gemini), corpus ID</li>
                </ul>
                <p className="text-xs text-yellow-700 mt-2">
                  Los campos "System Prompt", "Modo de Coaching", "Parámetros de IA" y "RAG" han sido movidos a sus respectivas pestañas.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === MOTOR GENÉRICO (SOLO LECTURA) === */}
        <div className="md:col-span-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <LucideIcons.Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  Motor Genérico Multi-Agente (Automático)
                </h4>
                <p className="text-xs text-blue-800 mb-3">
                  Este agente utiliza un motor conversacional compartido con reglas anti-repetición, 
                  manejo de RAG, progresión de diálogo y coherencia de rol. 
                  Estas reglas se aplican automáticamente y <strong>no requieren configuración manual</strong>.
                </p>
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-blue-700 hover:text-blue-900 mb-2">
                    Ver reglas del motor (solo lectura)
                  </summary>
                  <div className="mt-2 p-3 bg-white rounded border border-blue-100 font-mono text-xs text-gray-700 max-h-60 overflow-y-auto">
                    <div className="space-y-2">
                      <div><strong>✓ Coherencia de Rol:</strong> Nunca abandona el personaje definido</div>
                      <div><strong>✓ Anti-Repetición:</strong> Evita duplicar párrafos y estructuras consecutivas</div>
                      <div><strong>✓ Manejo RAG:</strong> Integra documentos externos sin contradecir escenarios</div>
                      <div><strong>✓ Mensajes Cortos:</strong> Responde conciso a inputs como "ok", "jaja", "?"</div>
                      <div><strong>✓ Progresión:</strong> Cada turno avanza la conversación un paso</div>
                      <div><strong>✓ Adaptación:</strong> Ajusta tono según fase y contexto de la negociación</div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOnboardingTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <LucideIcons.Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Configuración del Modal de Bienvenida
            </h4>
            <p className="text-xs text-blue-800">
              Este texto aparece <strong>solo en el modal inicial</strong>, antes de que comience el rol-play. 
              NO debe incluir información del caso ni el primer mensaje del cliente IA (ese se genera automáticamente).
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título del Modal
          </label>
          <input
            type="text"
            value={agentData.onboarding?.title || ''}
            onChange={(e) => updateField('onboarding.title', e.target.value)}
            disabled={mode === 'view'}
            maxLength={400}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Bienvenido al Simulador"
          />
          <p className="text-xs text-gray-500 mt-1">
            Máximo 400 caracteres. Ejemplo: "Bienvenido al Simulador de Negociación"
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mensaje de Bienvenida
          </label>
          <textarea
            value={agentData.onboarding?.message || ''}
            onChange={(e) => updateField('onboarding.message', e.target.value)}
            disabled={mode === 'view'}
            maxLength={400}
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="En esta práctica pondrás a prueba tus habilidades ejecutivas con un cliente IA."
          />
          <p className="text-xs text-gray-500 mt-1">
            Máximo 400 caracteres. Explica brevemente qué harán en la simulación.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mensaje de Consentimiento
          </label>
          <textarea
            value={agentData.onboarding?.consent || ''}
            onChange={(e) => updateField('onboarding.consent', e.target.value)}
            disabled={mode === 'view'}
            maxLength={400}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Tus respuestas serán evaluadas para generar métricas de desempeño."
          />
          <p className="text-xs text-gray-500 mt-1">
            Máximo 400 caracteres. Información sobre privacidad y evaluación.
          </p>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <LucideIcons.AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-yellow-900 mb-1">
              Importante: Separación de responsabilidades
            </h4>
            <ul className="text-xs text-yellow-800 space-y-1">
              <li>• <strong>Onboarding:</strong> Solo bienvenida y reglas (este modal)</li>
              <li>• <strong>Primer mensaje del Cliente IA:</strong> Se genera automáticamente usando el "Phase Prompt" de la fase inicial (State Machine)</li>
              <li>• <strong>NO incluyas aquí:</strong> Descripción del caso, ni el primer mensaje del cliente</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStateMachineTab = () => {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">State Machine - Fases Dinámicas</h3>
            <p className="text-sm text-gray-600 mt-1">
              Evolución táctica del comportamiento del Cliente IA por fases
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchitectureHelp(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
            >
              <LucideIcons.HelpCircle className="w-4 h-4" />
              ¿Cómo funciona?
            </button>
            <button
              onClick={() => updateField('stateMachine.enabled', !agentData.stateMachine.enabled)}
              disabled={mode === 'view'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                agentData.stateMachine.enabled 
                  ? 'bg-teal-600 text-white hover:bg-teal-700' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
            >
              {agentData.stateMachine.enabled ? '✓ Habilitado' : 'Deshabilitado'}
            </button>
          </div>
        </div>

        {/* Banner Informativo */}
        {agentData.stateMachine.enabled && (
          <div className="bg-slate-50 border-2 border-slate-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <LucideIcons.GitBranch className="w-5 h-5 text-slate-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-bold text-slate-900 mb-3">
                  Orden de Construcción del Prompt por Fase
                </h4>
                <div className="bg-white border border-slate-300 rounded-lg p-3">
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="bg-slate-700 text-white px-2 py-0.5 rounded font-bold text-xs">1</span>
                      <div className="flex-1">
                        <strong className="text-slate-900">Prompt de Entrada</strong>
                        <div className="text-slate-600 mt-0.5">Señal de inicio de fase (opcional)</div>
                      </div>
                    </div>
                    <div className="border-t border-slate-200 my-2"></div>
                    <div className="flex items-start gap-2">
                      <span className="bg-slate-700 text-white px-2 py-0.5 rounded font-bold text-xs">2</span>
                      <div className="flex-1">
                        <strong className="text-slate-900">Phase Prompt</strong>
                        <div className="text-slate-600 mt-0.5">Tácticas y comportamiento específico de la fase (requerido)</div>
                      </div>
                    </div>
                    <div className="border-t border-slate-200 my-2"></div>
                    <div className="flex items-start gap-2">
                      <span className="bg-slate-700 text-white px-2 py-0.5 rounded font-bold text-xs">3</span>
                      <div className="flex-1">
                        <strong className="text-slate-900">Prompt de Salida</strong>
                        <div className="text-slate-600 mt-0.5">Preparación para siguiente fase (opcional)</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-2">
                  💡 <strong>Recordatorio:</strong> El Phase Prompt se <strong>agrega</strong> al prompt base (IA Cliente + Escenario), no lo reemplaza.
                </div>
              </div>
            </div>
          </div>
        )}

        {agentData.stateMachine.enabled && (
          <>
            {/* Fase Inicial */}
            <div className="bg-slate-50 border border-slate-300 rounded-lg p-4">
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Fase de Inicio de la Simulación
              </label>
              <select
                value={agentData.stateMachine.initialPhase}
                onChange={(e) => updateField('stateMachine.initialPhase', e.target.value)}
                disabled={mode === 'view'}
                className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-600 text-sm"
              >
                <option value="">Seleccionar fase inicial...</option>
                {agentData.stateMachine.phases.map(phase => (
                  <option key={phase.phaseId} value={phase.phaseId}>
                    {phase.displayName} ({phase.phaseId})
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-600 mt-2">
                La simulación comenzará con esta fase. Las transiciones a siguientes fases son automáticas según turnos configurados.
              </p>
            </div>

            {/* Lista de Fases */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-bold text-slate-900">Fases Configuradas ({agentData.stateMachine.phases.length})</h4>
                {mode !== 'view' && (
                  <button
                    onClick={addPhase}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
                  >
                    <LucideIcons.Plus className="w-4 h-4" />
                    Agregar Fase
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {agentData.stateMachine.phases.map((phase, index) => (
                  <div key={`phase-${index}`} className="border-2 border-slate-300 rounded-xl p-6 bg-white shadow-sm">
                    {/* Header de la Fase */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-700 text-white rounded-full flex items-center justify-center font-bold text-lg">
                          {index + 1}
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-lg">Fase {index + 1}</h5>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {phase.displayName || 'Sin nombre definido'}
                          </p>
                        </div>
                      </div>
                      {mode !== 'view' && (
                        <button
                          onClick={() => removePhase(index)}
                          className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <LucideIcons.Trash2 className="w-4 h-4" />
                          <span className="text-sm font-medium">Eliminar</span>
                        </button>
                      )}
                    </div>

                    <div className="space-y-6">
                      {/* SECCIÓN: IDENTIFICACIÓN */}
                      <div className="border-l-4 border-slate-300 pl-4 bg-slate-50 py-4 pr-4 rounded-r">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-slate-700 text-white px-2 py-1 rounded text-xs font-bold">
                            IDENTIFICACIÓN
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                              Phase ID *
                            </label>
                            <input
                              type="text"
                              value={phase.phaseId || ''}
                              onChange={(e) => updatePhase(index, 'phaseId', e.target.value)}
                              disabled={mode === 'view'}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-600"
                              placeholder="OPENING"
                            />
                            <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1.5">
                              <LucideIcons.Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>MAYÚSCULAS_CON_GUIONES (ej: <code className="bg-slate-200 px-1 rounded">CLOSING_DEAL</code>)</span>
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                              Nombre Visible *
                            </label>
                            <input
                              type="text"
                              value={phase.displayName || ''}
                              onChange={(e) => updatePhase(index, 'displayName', e.target.value)}
                              disabled={mode === 'view'}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-slate-600"
                              placeholder="Apertura de la Negociación"
                            />
                            <p className="text-xs text-slate-500 mt-1.5 flex items-start gap-1.5">
                              <LucideIcons.Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>Nombre que verá el estudiante durante la simulación</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* SECCIÓN: CONFIGURACIÓN DE TURNOS */}
                      <div className="border-l-4 border-blue-700 pl-4 bg-blue-50 py-4 pr-4 rounded-r">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-blue-900 text-white px-2 py-1 rounded text-xs font-bold">
                            DURACIÓN
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                              Turnos Mínimos *
                            </label>
                            <input
                              type="number"
                              value={phase.minTurns || ''}
                              onChange={(e) => updatePhase(index, 'minTurns', parseInt(e.target.value) || 1)}
                              disabled={mode === 'view'}
                              className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600 bg-white"
                              min="1"
                              placeholder="2"
                            />
                            <p className="text-xs text-blue-800 mt-1.5">
                              Mínimo de intercambios antes de permitir transición a siguiente fase
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                              Turnos Máximos *
                            </label>
                            <input
                              type="number"
                              value={phase.maxTurns || ''}
                              onChange={(e) => updatePhase(index, 'maxTurns', parseInt(e.target.value) || 1)}
                              disabled={mode === 'view'}
                              className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600 bg-white"
                              min="1"
                              placeholder="10"
                            />
                            <p className="text-xs text-blue-800 mt-1.5">
                              Máximo de intercambios antes de forzar transición automática
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 🚫 PROMPT DE ENTRADA Y SALIDA REMOVIDOS - NO FORMAN PARTE DEL SKELETON FINAL
                          Razón: El experto confirmó que estos campos no se usan en construcción del prompt.
                          Solo se mantiene el phasePrompt principal que SÍ se usa. (TCK-UI-CLEANUP-001) */}

                      {/* SECCIÓN: PHASE PROMPT (PRINCIPAL) */}
                      <div className="border-l-4 border-slate-700 pl-4 bg-slate-50 py-4 pr-4 rounded-r">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="bg-slate-800 text-white px-2 py-1 rounded text-xs font-bold">
                            2. PHASE PROMPT (PRINCIPAL)
                          </span>
                          <span className="text-xs text-red-600 font-bold">* REQUERIDO</span>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-900 mb-1.5">
                            Tácticas y Comportamiento de esta Fase *
                          </label>
                          <textarea
                            value={phase.phasePrompt || ''}
                            onChange={(e) => updatePhase(index, 'phasePrompt', e.target.value)}
                            disabled={mode === 'view'}
                            rows={5}
                            className="w-full px-4 py-3 border-2 border-slate-400 rounded-lg text-sm font-mono focus:ring-2 focus:ring-slate-700 bg-white"
                            placeholder="FASE: OPENING&#10;- Muestra frustración inicial pero mantén profesionalismo&#10;- Deja claro el impacto económico del problema&#10;- NO aceptes disculpas genéricas&#10;- Exige plan de acción concreto con fechas"
                          />
                          <div className="bg-slate-100 border border-slate-300 rounded-lg p-3 mt-2">
                            <p className="text-xs text-slate-900 font-semibold mb-2">
                              Qué incluir aquí:
                            </p>
                            <ul className="text-xs text-slate-700 space-y-1 ml-4 list-disc">
                              <li>Tácticas específicas de esta fase</li>
                              <li>Qué debe/no debe hacer el cliente</li>
                              <li>Tipo de preguntas o presión a aplicar</li>
                              <li>Objetivo pedagógico de la fase</li>
                            </ul>
                            <p className="text-xs text-slate-900 font-semibold mt-3 mb-2">
                              No incluir:
                            </p>
                            <ul className="text-xs text-slate-700 space-y-1 ml-4 list-disc">
                              <li>Identidad del personaje (ya está en IA Cliente)</li>
                              <li>Descripción del caso (ya está en Escenarios)</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* 🚫 PROMPT DE SALIDA REMOVIDO - NO FORMA PARTE DEL SKELETON FINAL
                          (Ver comentario arriba sobre TCK-UI-CLEANUP-001) */}

                    </div>
                  </div>
                ))}

                {agentData.stateMachine.phases.length === 0 && (
                  <div className="text-center py-12 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50">
                    <LucideIcons.GitBranch className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p className="text-sm text-slate-600 font-medium">No hay fases configuradas</p>
                    <p className="text-xs text-slate-500 mt-1">Agrega una fase para comenzar a estructurar la simulación</p>
                    {mode !== 'view' && (
                      <button
                        onClick={addPhase}
                        className="mt-4 text-slate-700 hover:text-slate-900 text-sm font-medium"
                      >
                        + Agregar primera fase
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const addScenario = () => {
    setAgentData(prev => ({
      ...prev,
      scenarioTemplates: [
        ...prev.scenarioTemplates,
        {
          escenarioId: `scenario-${Date.now()}`,
          nombre: '',
          descripcion: '',
          descripcionContexto: '',
          objetivoPedagogico: '',
          tags: [],
          dificultad: 'medium',
          difficultyReason: '',
          industria: 'General'
        }
      ]
    }));
  };

  const removeScenario = (index) => {
    setAgentData(prev => ({
      ...prev,
      scenarioTemplates: prev.scenarioTemplates.filter((_, i) => i !== index)
    }));
  };

  const updateScenario = (index, field, value) => {
    setAgentData(prev => {
      const newScenarios = prev.scenarioTemplates.map((scenario, i) => 
        i === index ? { ...scenario, [field]: value } : scenario
      );
      return { ...prev, scenarioTemplates: newScenarios };
    });
  };

  const renderScenariosTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Escenarios de Entrenamiento</h3>
          <p className="text-sm text-gray-600 mt-1">
            Plantillas de casos que enfrentarán los estudiantes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowArchitectureHelp(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
          >
            <LucideIcons.HelpCircle className="w-4 h-4" />
            ¿Cómo funciona?
          </button>
          {mode !== 'view' && (
            <button 
              onClick={addScenario}
              className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
              <LucideIcons.Plus className="w-4 h-4" />
              Agregar Escenario
            </button>
          )}
        </div>
      </div>

      {agentData.scenarioTemplates.length === 0 ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <LucideIcons.FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">No hay escenarios configurados</p>
          {mode !== 'view' && (
            <button 
              onClick={addScenario}
              className="mt-4 text-indigo-600 hover:text-indigo-700 text-sm font-medium">
              + Agregar primer escenario
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {agentData.scenarioTemplates.map((scenario, index) => (
            <div key={`scenario-${index}-${scenario.escenarioId}`} className="border-2 border-gray-300 rounded-xl p-6 bg-white shadow-sm">
              {/* Header del Escenario */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {index + 1}
                  </div>
                  <div>
                    <h5 className="font-bold text-gray-900 text-lg">Escenario {index + 1}</h5>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {scenario.nombre || 'Sin nombre definido'}
                    </p>
                  </div>
                </div>
                {mode !== 'view' && (
                  <button
                    onClick={() => removeScenario(index)}
                    className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LucideIcons.Trash2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Eliminar</span>
                  </button>
                )}
              </div>

              <div className="space-y-6">
                {/* SECCIÓN 1: IDENTIFICACIÓN (Metadata - NO va al prompt) */}
                <div className="border-l-4 border-slate-300 pl-4 bg-slate-50 py-4 pr-4 rounded-r">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-slate-700 text-white px-2 py-1 rounded text-xs font-bold">
                      SECCIÓN 1
                    </span>
                    <h6 className="font-bold text-slate-900 text-sm">
                      Identificación del Escenario
                    </h6>
                    <span className="ml-auto bg-slate-200 text-slate-700 px-2 py-1 rounded text-xs font-medium">
                      📋 Solo metadata - NO va al prompt del IA
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          ID del Escenario *
                          <span className="ml-2 text-gray-500 font-normal">
                            (Identificador único)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={scenario.escenarioId}
                          onChange={(e) => updateScenario(index, 'escenarioId', e.target.value)}
                          disabled={mode === 'view'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
                          placeholder="sla-perdido-001"
                        />
                        <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1.5">
                          <LucideIcons.Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>Solo letras, números y guiones. Ej: <code className="bg-gray-100 px-1 rounded">reclamo-servicio-tech</code></span>
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                          Nombre del Escenario *
                          <span className="ml-2 text-gray-500 font-normal">
                            (Título visible)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={scenario.nombre}
                          onChange={(e) => updateScenario(index, 'nombre', e.target.value)}
                          disabled={mode === 'view'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
                          placeholder="SLA Perdido - Cliente Frustrado"
                        />
                        <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1.5">
                          <LucideIcons.Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>Aparece en la lista de escenarios para el estudiante</span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Descripción del Caso
                        <span className="ml-2 text-gray-500 font-normal">
                          (Resumen para la lista)
                        </span>
                      </label>
                      <textarea
                        value={scenario.descripcion}
                        onChange={(e) => updateScenario(index, 'descripcion', e.target.value)}
                        disabled={mode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
                        rows="2"
                        placeholder="El proveedor incumplió el SLA de servicio 3 veces consecutivas. El cliente busca compensación y garantías."
                      />
                      <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1.5">
                        <LucideIcons.Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>Resumen breve que verá el estudiante antes de iniciar la simulación</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* SECCIÓN 2: CONTEXTO SITUACIONAL (SÍ va al prompt - CRÍTICO) */}
                <div className="border-l-4 border-blue-800 pl-4 bg-blue-50 rounded-r-lg py-4 pr-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-blue-900 text-white px-2 py-1 rounded text-xs font-bold">
                      SECCIÓN 2
                    </span>
                    <h6 className="font-bold text-blue-900 text-sm">
                      Contexto Situacional del Cliente IA
                    </h6>
                    <span className="ml-auto bg-green-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <LucideIcons.CheckCircle className="w-3 h-3" />
                      ✅ IMPLEMENTADO - TCK-P0-V4-PROMPT-SCENARIO-CONTEXT
                    </span>
                  </div>
                  
                  {/* Badge informativo sobre implementación */}
                  <div className="bg-green-100 border-l-4 border-green-600 p-3 mb-3 rounded-r">
                    <p className="text-xs text-green-900 font-semibold flex items-center gap-1.5">
                      <LucideIcons.Sparkles className="w-4 h-4" />
                      Este campo ahora SÍ se inyecta en el prompt del Cliente IA (Motor V4)
                    </p>
                    <p className="text-xs text-green-800 mt-1">
                      Formato en prompt: <code className="bg-green-200 px-1 py-0.5 rounded">ESCENARIO / CONTEXTO: [tu texto]</code>
                    </p>
                    <p className="text-xs text-green-700 mt-1 italic">
                      Implementado: 12 enero 2026 - TurnOrchestratorV4.js líneas 948-983
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold text-blue-900 mb-1.5">
                      Instrucciones para el Cliente IA *
                      <span className="ml-2 text-blue-700 font-normal">
                        (Campo: <code className="bg-blue-200 px-1 rounded text-xs">context</code>)
                      </span>
                    </label>
                    <p className="text-xs text-blue-600 mb-2 flex items-center gap-1">
                      <LucideIcons.Info className="w-3 h-3" />
                      <strong>Schema V4:</strong> <code className="bg-blue-100 px-1 py-0.5 rounded">scenarioTemplates[].context</code>
                      <span className="text-blue-500">(legacy: descripcionContexto)</span>
                    </p>
                    <textarea
                      value={scenario.descripcionContexto}
                      onChange={(e) => updateScenario(index, 'descripcionContexto', e.target.value)}
                      disabled={mode === 'view'}
                      className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600 bg-white"
                      rows="4"
                      placeholder="Eres el CFO de TechCorp. El proveedor gestionaba un clúster crítico pero incumplió el SLA 3 veces en 2 meses, causando pérdidas de $50K. Buscas compensación económica y garantías técnicas antes de considerar la renovación."
                    />
                    <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mt-2">
                      <p className="text-xs text-blue-900 font-semibold mb-2 flex items-center gap-1.5">
                        <LucideIcons.Lightbulb className="w-4 h-4" />
                        ¿Qué escribir aquí?
                      </p>
                      <ul className="text-xs text-blue-800 space-y-1 ml-5 list-disc">
                        <li><strong>El PROBLEMA actual:</strong> Qué pasó que motivó este encuentro</li>
                        <li><strong>El IMPACTO:</strong> Cómo afectó al negocio o al cliente</li>
                        <li><strong>El OBJETIVO:</strong> Qué busca lograr el cliente en esta conversación</li>
                        <li><strong>El CONTEXTO:</strong> Información relevante del historial</li>
                      </ul>
                      <p className="text-xs text-blue-900 mt-3 bg-white border border-blue-300 rounded p-2 italic">
                        💡 <strong>Ejemplo:</strong> "El proveedor incumplió el SLA [PROBLEMA], causando $50K en pérdidas [IMPACTO]. Buscas compensación y garantías [OBJETIVO] antes de la renovación [CONTEXTO]."
                      </p>
                    </div>
                    <div className="bg-slate-50 border border-slate-300 rounded-lg p-2 mt-2">
                      <p className="text-xs text-slate-700 flex items-start gap-1.5">
                        <span className="font-bold">⚠️ NO repitas:</span> La identidad permanente del personaje (eso va en "IA Cliente" → Role Definition)
                      </p>
                    </div>
                  </div>
                </div>

                {/* SECCIÓN 3: OBJETIVO PEDAGÓGICO (SÍ va al prompt - IMPORTANTE) */}
                <div className="border-l-4 border-teal-700 pl-4 bg-teal-50 rounded-r-lg py-4 pr-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-teal-800 text-white px-2 py-1 rounded text-xs font-bold">
                      SECCIÓN 3
                    </span>
                    <h6 className="font-bold text-teal-900 text-sm">
                      Objetivo Pedagógico
                    </h6>
                    <span className="ml-auto bg-green-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                      <LucideIcons.CheckCircle className="w-3 h-3" />
                      ✅ IMPLEMENTADO - TCK-P0-V4-PROMPT-PEDAGOGICAL-GOAL
                    </span>
                  </div>
                  
                  {/* Badge informativo sobre implementación */}
                  <div className="bg-green-100 border-l-4 border-green-600 p-3 mb-3 rounded-r">
                    <p className="text-xs text-green-900 font-semibold flex items-center gap-1.5">
                      <LucideIcons.Sparkles className="w-4 h-4" />
                      Este campo ahora SÍ se inyecta en el prompt del Cliente IA (Motor V4)
                    </p>
                    <p className="text-xs text-green-800 mt-1">
                      Formato en prompt: <code className="bg-green-200 px-1 py-0.5 rounded">OBJETIVO PEDAGÓGICO (LO QUE SE PONE A PRUEBA): [tu texto]</code>
                    </p>
                    <p className="text-xs text-green-700 mt-1 italic">
                      Implementado: 12 enero 2026 - TurnOrchestratorV4.js líneas 984-1013
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-semibold text-teal-900 mb-1.5">
                      Habilidad que Practicará el Estudiante *
                      <span className="ml-2 text-teal-700 font-normal">
                        (Campo: <code className="bg-teal-200 px-1 rounded text-xs">pedagogicalGoal</code>)
                      </span>
                    </label>
                    <p className="text-xs text-teal-600 mb-2 flex items-center gap-1">
                      <LucideIcons.Info className="w-3 h-3" />
                      <strong>Schema V4:</strong> <code className="bg-teal-100 px-1 py-0.5 rounded">scenarioTemplates[].pedagogicalGoal</code>
                      <span className="text-teal-500">(legacy: objetivoPedagogico)</span>
                    </p>
                    <textarea
                      value={scenario.objetivoPedagogico || ''}
                      onChange={(e) => updateScenario(index, 'objetivoPedagogico', e.target.value)}
                      disabled={mode === 'view'}
                      className="w-full px-4 py-3 border-2 border-teal-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-700 bg-white"
                      rows="3"
                      placeholder="Practicar manejo de conflictos en negociación ejecutiva. Contener a un cliente frustrado sin ser defensivo. Ofrecer plan de acción concreto sin comprometer plazos irreales."
                    />
                    <div className="bg-teal-100 border border-teal-300 rounded-lg p-3 mt-2">
                      <p className="text-xs text-teal-900 font-semibold mb-2 flex items-center gap-1.5">
                        <LucideIcons.Target className="w-4 h-4" />
                        Propósito de este campo
                      </p>
                      <p className="text-xs text-teal-800">
                        El Cliente IA conoce qué habilidad está practicando el estudiante, lo que le permite:
                      </p>
                      <ul className="text-xs text-teal-800 space-y-1 ml-5 list-disc mt-2">
                        <li>Crear desafíos relevantes para esa habilidad específica</li>
                        <li>Ajustar su comportamiento para que la práctica sea efectiva</li>
                        <li>Proporcionar oportunidades para demostrar competencia</li>
                      </ul>
                      <p className="text-xs text-teal-900 mt-3 bg-white border border-teal-300 rounded p-2 italic">
                        💡 <strong>Ejemplo:</strong> Si el objetivo es "gestión de objeciones", el cliente IA pondrá objeciones realistas pero superables.
                      </p>
                    </div>
                  </div>
                </div>

                {/* SECCIÓN 4: METADATA ADICIONAL (NO va al prompt) */}
                <div className="border-l-4 border-slate-300 pl-4 bg-slate-50 py-4 pr-4 rounded-r">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-slate-600 text-white px-2 py-1 rounded text-xs font-bold">
                      SECCIÓN 4
                    </span>
                    <h6 className="font-bold text-slate-900 text-sm">
                      Metadata y Organización
                    </h6>
                    <span className="ml-auto bg-slate-200 text-slate-700 px-2 py-1 rounded text-xs font-medium">
                      📋 Solo para filtros - NO va al prompt
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Industria
                        <span className="ml-2 text-gray-500 font-normal">
                          (Para categorización)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={scenario.industria || ''}
                        onChange={(e) => updateScenario(index, 'industria', e.target.value)}
                        disabled={mode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
                        placeholder="Tecnología, Salud, Retail..."
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Ayuda al estudiante a contextualizar el sector del caso
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Tags
                        <span className="ml-2 text-gray-500 font-normal">
                          (Separados por coma)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={scenario.tags?.join(', ') || ''}
                        onChange={(e) => updateScenario(index, 'tags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                        disabled={mode === 'view'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-500"
                        placeholder="negociacion, salario, laboral"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Etiquetas para búsqueda y filtrado de escenarios
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderClientConfigTab = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <LucideIcons.UserCircle className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Configuración del Cliente Simulado (Motor V3)
        </h3>
      </div>

      {/* Guía Rápida */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <LucideIcons.Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium mb-1">
              Guía Rápida
            </p>
            <p className="text-xs text-blue-800">
              Completa los campos <strong>en orden de arriba hacia abajo</strong>. Cada sección afecta las siguientes, 
              así que define primero la identidad antes del escenario y las fases.
            </p>
          </div>
        </div>
      </div>

      {/* Habilitar Cliente */}
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <input
          type="checkbox"
          id="clientEnabled"
          checked={agentData.clientConfig?.enabled !== false}
          onChange={(e) => setAgentData({
            ...agentData,
            clientConfig: { ...agentData.clientConfig, enabled: e.target.checked }
          })}
          className="w-5 h-5 text-blue-600 rounded"
          disabled={mode === 'view'}
        />
        <label htmlFor="clientEnabled" className="text-sm font-medium text-gray-700">
          Habilitar simulación de cliente con IA
        </label>
      </div>

      {agentData.clientConfig?.enabled !== false && (
        <>
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 1: IDENTIDAD DEL PERSONAJE */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-indigo-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-indigo-500 text-white rounded-full font-bold text-sm">
                1
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Identidad del Personaje
              </h4>
              <span className="ml-auto bg-green-600 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                <LucideIcons.CheckCircle className="w-3 h-3" />
                ✅ IMPLEMENTADO - TCK-P0-V4-PROMPT-EXPLICIT-ROLES-LABELS
              </span>
            </div>

            {/* Badge informativo sobre implementación */}
            <div className="bg-green-100 border-l-4 border-green-600 p-3 mb-3 rounded-r">
              <p className="text-xs text-green-900 font-semibold flex items-center gap-1.5">
                <LucideIcons.Sparkles className="w-4 h-4" />
                Los roles ahora SÍ se inyectan explícitamente en el prompt del Cliente IA
              </p>
              <p className="text-xs text-green-800 mt-1">
                Formato en prompt: <code className="bg-green-200 px-1 py-0.5 rounded">TU ROL (CLIENTE): [clientRole]</code> + <code className="bg-green-200 px-1 py-0.5 rounded">ROL DEL PARTICIPANTE: [participantRole]</code>
              </p>
              <p className="text-xs text-green-700 mt-1 italic">
                Implementado: 12 enero 2026 - TurnOrchestratorV4.js líneas 1014-1047
              </p>
            </div>

            {/* Banner de contexto */}
            <div className="bg-indigo-50 border-l-4 border-indigo-400 p-3 mb-4">
              <p className="text-xs text-indigo-800">
                <LucideIcons.Info className="inline w-3 h-3 mr-1" />
                Primero define <strong>QUIÉN es</strong> el cliente simulado. Esto aparecerá en el system prompt como su identidad.
              </p>
            </div>

            {/* Rol del Cliente (MOVIDO DESDE EVALUACIÓN) */}
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <LucideIcons.Tag className="w-4 h-4 text-indigo-600" />
                Rol del Cliente
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={agentData.labels?.clientRole || 'CLIENTE'}
                onChange={(e) => setAgentData({
                  ...agentData,
                  labels: { ...agentData.labels, clientRole: e.target.value }
                })}
                disabled={mode === 'view'}
                placeholder="Ej: CLIENTE, PACIENTE, COMPRADOR, GERENTE"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
              />
              <p className="text-xs text-gray-600 mt-1.5">
                💡 <strong>Cómo se llama este personaje</strong> en la simulación (ej: CLIENTE, PACIENTE, COMPRADOR).
              </p>
              <p className="text-xs text-amber-700 mt-1">
                ⚠️ Este nombre aparece en el prompt del sistema: "TU ROL: Eres el <strong>[{agentData.labels?.clientRole || 'CLIENTE'}]</strong>"
              </p>
            </div>

            {/* Definición del Rol */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <LucideIcons.FileText className="w-4 h-4 text-indigo-600" />
                Definición del Rol
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={agentData.clientConfig?.roleDefinition || ''}
                onChange={(e) => setAgentData({
                  ...agentData,
                  clientConfig: { ...agentData.clientConfig, roleDefinition: e.target.value }
                })}
                disabled={mode === 'view'}
                rows={7}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm disabled:bg-gray-100"
                placeholder="Eres Laura Martínez, CFO de RetailCorp con 10 años de experiencia en retail. Tu personalidad es analítica y cautelosa..."
              />
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">📋 Plantilla Sugerida:</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• <strong>Nombre y cargo:</strong> [Nombre], [Puesto] de [Empresa]</li>
                  <li>• <strong>Experiencia:</strong> [X años] en [sector/industria]</li>
                  <li>• <strong>Personalidad:</strong> [adjetivos clave: analítica, cautelosa, impulsiva...]</li>
                  <li>• <strong>Contexto actual:</strong> [situación/presión/objetivos inmediatos]</li>
                  <li>• <strong>Estilo de comunicación:</strong> [formal/directo/evasivo]</li>
                  <li>• <strong>Motivaciones:</strong> [qué busca lograr en esta interacción]</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 2: COMPORTAMIENTO Y ACTITUD */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-purple-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-purple-500 text-white rounded-full font-bold text-sm">
                2
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Comportamiento y Actitud
              </h4>
            </div>

            {/* Banner de dependencia */}
            <div className="bg-purple-50 border-l-4 border-purple-400 p-3 mb-4">
              <p className="text-xs text-purple-800">
                <LucideIcons.Link className="inline w-3 h-3 mr-1" />
                Define <strong>CÓMO se comporta</strong> el cliente durante la simulación. 
                Esto afectará el escenario que configurarás en la sección 3.
              </p>
            </div>

            {/* Nivel de Dificultad */}
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <LucideIcons.Gauge className="w-4 h-4 text-purple-600" />
                Nivel de Dificultad
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['low', 'medium', 'high'].map((level) => (
                  <button
                    key={level}
                    onClick={() => setAgentData({
                      ...agentData,
                      clientConfig: { ...agentData.clientConfig, difficultyLevel: level }
                    })}
                    disabled={mode === 'view'}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      (agentData.clientConfig?.difficultyLevel || 'medium') === level
                        ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {level === 'low' ? 'Fácil' : level === 'medium' ? 'Medio' : 'Difícil'}
                  </button>
                ))}
              </div>
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">📊 Impacto de cada nivel:</p>
                <div className="space-y-1.5 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-green-600 min-w-[60px]">FÁCIL:</span>
                    <span>Cliente colaborativo, acepta argumentos razonables, busca soluciones.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-amber-600 min-w-[60px]">MEDIO:</span>
                    <span>Cliente crítico pero razonable, requiere buenos argumentos (Recomendado).</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-red-600 min-w-[60px]">DIFÍCIL:</span>
                    <span>Cliente hostil o muy exigente, requiere negociación avanzada.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Instrucciones de Comportamiento (NUEVO CAMPO) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <LucideIcons.Settings className="w-4 h-4 text-purple-600" />
                Instrucciones de Comportamiento
                <span className="text-xs font-normal text-gray-500">(Opcional)</span>
              </label>
              <textarea
                value={agentData.clientConfig?.behaviorInstructions || ''}
                onChange={(e) => setAgentData({
                  ...agentData,
                  clientConfig: { ...agentData.clientConfig, behaviorInstructions: e.target.value }
                })}
                disabled={mode === 'view'}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm disabled:bg-gray-100"
                placeholder="- Haz preguntas de seguimiento cuando algo no te convenza&#10;- Solicita datos concretos, no aceptes promesas genéricas&#10;- Si detectas contradicciones, señálalas con tacto&#10;- Mantén un tono profesional pero firme"
              />
              <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">💡 Ejemplos útiles:</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4">
                  <li>• "Interrumpe educadamente si el participante habla más de 30 segundos"</li>
                  <li>• "Muestra impaciencia si no respondes directamente su objeción"</li>
                  <li>• "Relájate gradualmente si el participante gana tu confianza"</li>
                  <li>• "Evita ser condescendiente, mantén curiosidad genuina"</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 3: CONTEXTO SITUACIONAL (ESCENARIO) */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-amber-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-amber-500 text-white rounded-full font-bold text-sm">
                3
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Contexto Situacional (Escenario)
              </h4>
            </div>

            {/* Banner de dependencia */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4">
              <p className="text-xs text-amber-900">
                <LucideIcons.AlertTriangle className="inline w-3 h-3 mr-1" />
                <strong>DEPENDENCIA:</strong> El escenario usa el Rol y Comportamiento definidos arriba.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                ⚠️ El escenario debe <strong>complementar</strong> la identidad, NO contradecirla.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                <LucideIcons.FileText className="inline w-4 h-4 mr-1 text-amber-600" />
                Los escenarios se configuran en la pestaña <strong>"Escenarios"</strong>
              </p>
              <p className="text-xs text-gray-600 mb-3">
                Un escenario define la <strong>situación específica</strong> que vive el cliente (ej: negociación de presupuesto Q4, 
                crisis financiera en startup, evaluación de proveedor).
              </p>
              <button
                onClick={() => setActiveTab('scenarios')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
              >
                <LucideIcons.ExternalLink className="w-4 h-4" />
                Ir a gestionar escenarios
              </button>
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-xs font-semibold text-gray-700 mb-1">💡 Tip de coherencia:</p>
                <p className="text-xs text-gray-600">
                  Crea escenarios que se alineen con el <strong>"{agentData.labels?.clientRole || 'CLIENTE'}"</strong> definido arriba. 
                  Si es un CFO, el escenario debe involucrar decisiones financieras.
                </p>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 4: EVOLUCIÓN DE LA CONVERSACIÓN (STATE MACHINE) */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-green-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-green-500 text-white rounded-full font-bold text-sm">
                4
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Evolución de la Conversación (State Machine)
              </h4>
            </div>

            {/* Banner de dependencia */}
            <div className="bg-green-50 border-l-4 border-green-400 p-3 mb-4">
              <p className="text-xs text-green-900">
                <LucideIcons.GitBranch className="inline w-3 h-3 mr-1" />
                <strong>DEPENDENCIA:</strong> Las fases usan el Escenario como contexto.
              </p>
              <p className="text-xs text-green-800 mt-1">
                ⚠️ Si cambias el escenario, revisa que las fases sigan siendo coherentes.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-2">
                <LucideIcons.GitBranch className="inline w-4 h-4 mr-1 text-green-600" />
                Las fases se configuran en la pestaña <strong>"State Machine"</strong>
              </p>
              <p className="text-xs text-gray-600 mb-3">
                La State Machine estructura la conversación en <strong>fases progresivas</strong> (ej: Apertura → Exploración → Propuesta → Cierre).
                Cada fase tiene prompts específicos que guían el comportamiento del cliente.
              </p>
              <button
                onClick={() => setActiveTab('stateMachine')}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
              >
                <LucideIcons.ExternalLink className="w-4 h-4" />
                Ir a configurar fases
              </button>
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-xs font-semibold text-gray-700 mb-1">💡 Tip de coherencia:</p>
                <p className="text-xs text-gray-600">
                  Las fases deben seguir el flujo natural del escenario seleccionado. 
                  Una negociación tiene fases distintas a una consulta médica.
                </p>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 5: MEMORIA Y CONTEXTO */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-cyan-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-cyan-500 text-white rounded-full font-bold text-sm">
                5
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Memoria y Contexto
              </h4>
            </div>

            {/* Ventana de Memoria (MOVIDO DESDE PESTAÑA MEMORIA) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <LucideIcons.Database className="w-4 h-4 text-cyan-600" />
                  Ventana de Memoria del Cliente
                </label>
                <span className="text-lg font-semibold text-cyan-600">
                  {agentData.memoryConfig?.clientMemoryWindow || 10} turnos
                </span>
              </div>
              <input
                type="range"
                min="2"
                max="50"
                value={agentData.memoryConfig?.clientMemoryWindow || 10}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value);
                  setAgentData(prev => ({
                    ...prev,
                    memoryConfig: {
                      ...prev.memoryConfig,
                      clientMemoryWindow: newValue
                    }
                  }));
                }}
                disabled={mode === 'view'}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>2 turnos (mínimo)</span>
                <span>50 turnos (máximo)</span>
              </div>
              <p className="text-xs text-gray-600 mt-2">
                💡 <strong>Cuántos mensajes previos</strong> recuerda el cliente al generar respuestas. Default: 10 turnos.
              </p>

              {/* Guía de impacto */}
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">💰 Impacto en costos:</p>
                <div className="space-y-1.5 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-green-600 min-w-[90px]">2-10 turnos:</span>
                    <span>Económico y suficiente para la mayoría de casos (Recomendado)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-amber-600 min-w-[90px]">11-20 turnos:</span>
                    <span>Moderado, útil para conversaciones complejas</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-medium text-red-600 min-w-[90px]">21-50 turnos:</span>
                    <span>Alto consumo de tokens y costo por llamada</span>
                  </div>
                </div>
              </div>

              {/* Advertencia de costo alto */}
              {(agentData.memoryConfig?.clientMemoryWindow || 10) > 15 && (
                <div className="mt-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <LucideIcons.AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      <strong>⚠️ Alto consumo de tokens:</strong> {agentData.memoryConfig?.clientMemoryWindow} turnos en memoria 
                      incrementa significativamente el costo por llamada a OpenAI.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* SECCIÓN 6: PARÁMETROS TÉCNICOS */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-gray-500 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-8 h-8 bg-gray-500 text-white rounded-full font-bold text-sm">
                6
              </span>
              <h4 className="text-base font-semibold text-gray-900">
                Parámetros Técnicos
              </h4>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-700">
                <LucideIcons.Settings className="inline w-3 h-3 mr-1" />
                Configuración avanzada del modelo de IA. Los valores por defecto funcionan bien en el 90% de casos.
              </p>
            </div>

            {/* Proveedor y Modelo */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Proveedor IA
                </label>
                <select
                  value={agentData.clientConfig?.provider || 'openai'}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    clientConfig: { ...agentData.clientConfig, provider: e.target.value }
                  })}
                  disabled={mode === 'view'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                >
                  <option value="openai">OpenAI</option>
                  <option value="gemini">Google Gemini</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo
                </label>
                <select
                  value={agentData.clientConfig?.model || 'gpt-4o-mini'}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    clientConfig: { ...agentData.clientConfig, model: e.target.value }
                  })}
                  disabled={mode === 'view'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                >
                  <option value="gpt-4o-mini">GPT-4o-mini (Recomendado)</option>
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4">GPT-4</option>
                </select>
              </div>
            </div>

            {/* Temperatura y Tokens */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <LucideIcons.Thermometer className="w-4 h-4 text-gray-600" />
                  Temperatura: {(agentData.clientConfig?.temperature || 0.7).toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={agentData.clientConfig?.temperature || 0.7}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    clientConfig: { ...agentData.clientConfig, temperature: parseFloat(e.target.value) }
                  })}
                  disabled={mode === 'view'}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-600"
                />
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-xs text-gray-600">
                    💡 <strong>Recomendado: 0.6-0.8</strong> para simulaciones realistas
                  </p>
                  <ul className="text-xs text-gray-600 mt-1 space-y-0.5 ml-3">
                    <li>• 0.0-0.3: Predecible y consistente</li>
                    <li>• 0.4-0.7: Balance realismo/control</li>
                    <li>• 0.8-2.0: Creativo pero impredecible</li>
                  </ul>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <LucideIcons.Hash className="w-4 h-4 text-gray-600" />
                  Máx. Tokens
                </label>
                <input
                  type="number"
                  value={agentData.clientConfig?.maxTokens || 300}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    clientConfig: { ...agentData.clientConfig, maxTokens: parseInt(e.target.value) }
                  })}
                  disabled={mode === 'view'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                />
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-xs text-gray-600">
                    💡 Longitud máxima de cada respuesta del cliente
                  </p>
                  <ul className="text-xs text-gray-600 mt-1 space-y-0.5 ml-3">
                    <li>• 150-250: Respuestas concisas</li>
                    <li>• 300-400: Conversación natural</li>
                    <li>• 500+: Respuestas muy elaboradas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* RESUMEN DE CONFIGURACIÓN */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="border-t-4 border-blue-500 pt-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5">
              <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                <LucideIcons.CheckCircle className="w-5 h-5" />
                Resumen de Configuración
              </h4>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <span className={agentData.labels?.clientRole && agentData.clientConfig?.roleDefinition ? 'text-green-600' : 'text-gray-400'}>
                    {agentData.labels?.clientRole && agentData.clientConfig?.roleDefinition ? '✓' : '○'}
                  </span>
                  <span className="text-gray-700">
                    <strong>Identidad:</strong> {agentData.labels?.clientRole || 'Sin definir'} 
                    {agentData.clientConfig?.roleDefinition ? ` (${agentData.clientConfig.roleDefinition.slice(0, 50)}...)` : ''}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700">
                    <strong>Comportamiento:</strong> Dificultad {agentData.clientConfig?.difficultyLevel === 'low' ? 'Fácil' : agentData.clientConfig?.difficultyLevel === 'high' ? 'Difícil' : 'Media'}
                    {agentData.clientConfig?.behaviorInstructions && ' + Instrucciones adicionales'}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600">ℹ️</span>
                  <span className="text-gray-700">
                    <strong>Escenario:</strong> Configurable en pestaña "Escenarios"
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600">ℹ️</span>
                  <span className="text-gray-700">
                    <strong>Fases:</strong> Configurable en pestaña "State Machine"
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700">
                    <strong>Memoria:</strong> {agentData.memoryConfig?.clientMemoryWindow || 10} turnos
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span className="text-gray-700">
                    <strong>Parámetros técnicos:</strong> {agentData.clientConfig?.model || 'gpt-4o-mini'}, T={agentData.clientConfig?.temperature || 0.7}
                  </span>
                </div>
              </div>
              {(!agentData.labels?.clientRole || !agentData.clientConfig?.roleDefinition) && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs text-amber-700 font-medium">
                    ⚠️ Completa los campos obligatorios (*) en la Sección 1: Identidad
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderCoachConfigTab = () => {
    const coachMode = agentData.coachConfig?.mode || 'on-demand';
    const showFrequency = coachMode === 'automatic' || coachMode === 'hybrid';
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <LucideIcons.GraduationCap className="w-6 h-6 text-teal-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Configuración del Coach Evaluador (Motor V3)
            </h3>
          </div>
          <span className="text-xs text-gray-500">Powered by Google Gemini 2.0 Flash</span>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="p-4 bg-teal-50 border-2 border-teal-300 rounded-lg">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="coachEnabled"
              checked={agentData.coachConfig?.enabled !== false}
              onChange={(e) => setAgentData({
                ...agentData,
                coachConfig: { ...agentData.coachConfig, enabled: e.target.checked }
              })}
              disabled={mode === 'view'}
              className="w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
            />
            <div className="flex-1">
              <label htmlFor="coachEnabled" className="text-sm font-semibold text-gray-900 block">
                Habilitar Coach Pedagógico durante la simulación
              </label>
              <p className="text-xs text-gray-600 mt-1">
                Controla si el coach puede intervenir DURANTE la conversación. 
                La <strong>evaluación final</strong> siempre se ejecuta al completar, independientemente de esta opción.
              </p>
            </div>
          </div>
        </div>

        {agentData.coachConfig?.enabled !== false && (
          <>
            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 1: IDENTIDAD DEL COACH */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-slate-700 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-slate-700 text-white rounded-full font-bold text-sm">
                  1
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Identidad del Coach
                </h4>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.User className="inline w-3 h-3 mr-1" />
                  Define quién es el coach: su expertise, experiencia y enfoque pedagógico
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt Base del Coach *
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    Este prompt se combina con el contexto de la simulación y el corpus RAG
                  </span>
                </label>
                <textarea
                  value={agentData.coachConfig?.promptBase || ''}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    coachConfig: { ...agentData.coachConfig, promptBase: e.target.value }
                  })}
                  disabled={mode === 'view'}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono text-sm disabled:bg-gray-100"
                  placeholder="Eres un coach experto en negociación B2B con 15 años de experiencia en tecnología. Tu rol es evaluar el desempeño del estudiante y proporcionar feedback constructivo basado en metodologías como BATNA, ZOPA y pensamiento crítico..."
                />
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>💡 Tip:</strong> Incluye expertise específica (años de experiencia, industria), 
                    metodologías que domina (BATNA, ZOPA) y su estilo de feedback (constructivo, directo, empático)
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 2: MODO DE EJECUCIÓN */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-teal-700 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-teal-700 text-white rounded-full font-bold text-sm">
                  2
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Modo de Ejecución
                </h4>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.Settings className="inline w-3 h-3 mr-1" />
                  Controla cuándo y cómo se ejecuta el coach durante la simulación
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Modo de Ejecución *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { 
                      value: 'on-demand', 
                      label: 'On-Demand', 
                      desc: 'Solo cuando el usuario presiona el botón de ayuda',
                      icon: '🎯'
                    },
                    { 
                      value: 'automatic', 
                      label: 'Automático', 
                      desc: 'Evalúa cada N turnos automáticamente',
                      icon: '⚡'
                    },
                    { 
                      value: 'hybrid', 
                      label: 'Híbrido', 
                      desc: 'Automático cada N turnos + on-demand disponible',
                      icon: '🔄'
                    }
                  ].map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setAgentData({
                        ...agentData,
                        coachConfig: { ...agentData.coachConfig, mode: m.value }
                      })}
                      disabled={mode === 'view'}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        coachMode === m.value
                          ? 'bg-teal-600 text-white border-teal-600 shadow-lg'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-teal-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="text-2xl mb-2">{m.icon}</div>
                      <div className="font-semibold text-sm mb-1">{m.label}</div>
                      <div className={`text-xs ${
                        coachMode === m.value ? 'text-teal-100' : 'text-gray-500'
                      }`}>{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frecuencia (solo si es automatic o hybrid) */}
              {showFrequency && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <LucideIcons.Clock className="inline w-4 h-4 mr-1 text-amber-600" />
                    Frecuencia de Evaluación (cada N turnos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={agentData.coachConfig?.evaluationFrequency || 2}
                    onChange={(e) => setAgentData({
                      ...agentData,
                      coachConfig: { ...agentData.coachConfig, evaluationFrequency: parseInt(e.target.value) }
                    })}
                    disabled={mode === 'view'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 disabled:bg-gray-100"
                  />
                  <p className="text-xs text-amber-700 mt-2">
                    💡 El coach evaluará el desempeño cada {agentData.coachConfig?.evaluationFrequency || 2} mensajes del estudiante
                  </p>
                </div>
              )}

              {!showFrequency && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <LucideIcons.Info className="inline w-3 h-3 mr-1" />
                    <strong>Modo On-Demand:</strong> El coach solo se ejecuta cuando el estudiante presiona el botón "Pedir ayuda al coach". 
                    No hay evaluaciones automáticas.
                  </p>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 3: NIVEL DE INTERVENCIÓN */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-blue-700 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-blue-700 text-white rounded-full font-bold text-sm">
                  3
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Nivel de Intervención
                </h4>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.Gauge className="inline w-3 h-3 mr-1" />
                  Define qué tan directo y detallado es el feedback del coach
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Modo de Intervención *
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'silent', label: 'Silencioso', desc: 'Coach desactivado' },
                    { value: 'minimal', label: 'Mínimo', desc: 'Breve, sutil, discreto' },
                    { value: 'balanced', label: 'Equilibrado', desc: 'Constructivo y claro' },
                    { value: 'active', label: 'Activo', desc: 'Detallado y didáctico' }
                  ].map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setAgentData({
                        ...agentData,
                        coachConfig: { ...agentData.coachConfig, interventionMode: m.value }
                      })}
                      disabled={mode === 'view'}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        (agentData.coachConfig?.interventionMode || 'balanced') === m.value
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="font-semibold text-sm">{m.label}</div>
                      <div className={`text-xs mt-1 ${
                        (agentData.coachConfig?.interventionMode || 'balanced') === m.value 
                          ? 'text-blue-100' 
                          : 'text-gray-500'
                      }`}>{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 4: PARÁMETROS TÉCNICOS */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-slate-500 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-slate-500 text-white rounded-full font-bold text-sm">
                  4
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Parámetros Técnicos
                </h4>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.Sliders className="inline w-3 h-3 mr-1" />
                  Configuración avanzada del modelo de IA
                </p>
              </div>

              {/* Temperatura */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <LucideIcons.Thermometer className="inline w-4 h-4 mr-1 text-gray-600" />
                  Temperatura: {(agentData.coachConfig?.temperature || 0.3).toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={agentData.coachConfig?.temperature || 0.3}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    coachConfig: { ...agentData.coachConfig, temperature: parseFloat(e.target.value) }
                  })}
                  disabled={mode === 'view'}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2">
                  <p className="text-xs text-gray-600">
                    💡 <strong>Recomendado: 0.3</strong> para feedback consistente y profesional
                  </p>
                  <ul className="text-xs text-gray-600 mt-1 space-y-0.5 ml-3">
                    <li>• 0.0-0.2: Muy consistente, predecible</li>
                    <li>• 0.3-0.5: Equilibrado, recomendado</li>
                    <li>• 0.6-1.0: Más creativo, menos consistente</li>
                  </ul>
                </div>
              </div>

              {/* Max Tokens Base (Coaching) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <LucideIcons.MessageSquare className="inline w-4 h-4 mr-1 text-gray-600" />
                  Max Tokens Coaching (durante simulación): {agentData.coachConfig?.maxTokens || 800}
                </label>
                <input
                  type="range"
                  min="800"
                  max="2800"
                  step="200"
                  value={agentData.coachConfig?.maxTokens || 800}
                  onChange={(e) => updateField('coachConfig.maxTokens', parseInt(e.target.value))}
                  disabled={mode === 'view'}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>800 (mínimo thinking)</span>
                  <span>2800 (muy detallado)</span>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <p className="text-xs text-blue-800">
                    💡 <strong>800 tokens:</strong> ~480 thinking + ~320 output | <strong>Recomendado: 1200-1600</strong>
                  </p>
                </div>
              </div>

              {/* Multiplicador para Evaluaciones Finales */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <LucideIcons.Award className="inline w-4 h-4 mr-1 text-gray-600" />
                  Multiplicador para Evaluaciones Finales
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {[1.5, 2, 2.5, 3, 3.5, 4].map((mult) => {
                    const isSelected = (agentData.coachConfig?.evaluationTokenMultiplier || 2.5) === mult;
                    const baseTokens = agentData.coachConfig?.maxTokens || 500;
                    const finalTokens = Math.round(baseTokens * mult);
                    return (
                      <button
                        key={mult}
                        onClick={() => updateField('coachConfig.evaluationTokenMultiplier', mult)}
                        disabled={mode === 'view'}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          isSelected
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <div className="font-bold text-lg">{mult}x</div>
                        <div className={`text-xs mt-1 ${
                          isSelected ? 'text-indigo-100' : 'text-gray-500'
                        }`}>{finalTokens}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <p className="text-xs text-indigo-800">
                    <strong>🎯 Evaluación final usará:</strong> {agentData.coachConfig?.maxTokens || 800} tokens × {agentData.coachConfig?.evaluationTokenMultiplier || 2.5} = <strong>{Math.round((agentData.coachConfig?.maxTokens || 800) * (agentData.coachConfig?.evaluationTokenMultiplier || 2.5))} tokens</strong>
                  </p>
                  <ul className="text-xs text-indigo-700 mt-2 space-y-1 ml-3">
                    <li>• <strong>1.5x-2x:</strong> Evaluaciones concisas (economía)</li>
                    <li>• <strong>2.5x-3x:</strong> Evaluaciones balanceadas (recomendado)</li>
                    <li>• <strong>3.5x-4x:</strong> Evaluaciones exhaustivas con ejemplos</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 5: BASE DE CONOCIMIENTO (RAG) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-amber-600 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-amber-600 text-white rounded-full font-bold text-sm">
                  5
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Base de Conocimiento (RAG)
                </h4>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.BookOpen className="inline w-3 h-3 mr-1" />
                  Permite al coach fundamentar su feedback en fuentes expertas específicas
                </p>
              </div>

              {/* Enable RAG */}
              <div className="flex items-center gap-3 p-4 bg-amber-100 border-2 border-amber-300 rounded-lg">
                <input
                  type="checkbox"
                  id="ragEnabled"
                  checked={agentData.coachConfig?.ragEnabled !== false}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    coachConfig: { ...agentData.coachConfig, ragEnabled: e.target.checked }
                  })}
                  disabled={mode === 'view'}
                  className="w-5 h-5 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                />
                <div className="flex-1">
                  <label htmlFor="ragEnabled" className="text-sm font-medium text-gray-900 block">
                    Habilitar base de conocimiento (RAG)
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    El coach consultará el corpus para fundamentar su feedback en fuentes expertas
                  </p>
                </div>
              </div>

              {/* RAG Configuration */}
              {agentData.coachConfig?.ragEnabled !== false && (
                <div className="space-y-4 p-4 bg-white border border-amber-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Corpus ID *
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ID del corpus en Google Vertex AI
                      </span>
                    </label>
                    <input
                      type="text"
                      value={agentData.coachConfig?.corpusId || ''}
                      onChange={(e) => updateField('coachConfig.corpusId', e.target.value)}
                      disabled={mode === 'view'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono text-xs disabled:bg-gray-100"
                      placeholder="projects/edutechpulselab-agent/locations/us-west1/ragCorpora/6917529027641081856"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Formato: projects/PROJECT_ID/locations/LOCATION/ragCorpora/CORPUS_ID
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vertex Project ID
                  </label>
                  <input
                    type="text"
                    value={agentData.coachConfig?.vertexProjectId || ''}
                    onChange={(e) => updateField('coachConfig.vertexProjectId', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="edutechpulselab-agent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vertex Location
                  </label>
                  <input
                    type="text"
                    value={agentData.coachConfig?.vertexLocation || ''}
                    onChange={(e) => updateField('coachConfig.vertexLocation', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder="us-west1"
                  />
                </div>
              </div>

                  {/* Metodologías del Coach */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Metodologías / Conceptos Clave
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        Conceptos que el coach puede referenciar en el feedback
                      </span>
                    </label>
                    <textarea
                      value={agentData.coachConfig?.methodologies?.join(', ') || ''}
                      onChange={(e) => updateField('coachConfig.methodologies', 
                        e.target.value.split(',').map(m => m.trim()).filter(Boolean)
                      )}
                      disabled={mode === 'view'}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm disabled:bg-gray-100"
                      rows="2"
                      placeholder="BATNA, ZOPA, Pensamiento crítico, Comunicación asertiva"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      📚 Separa múltiples metodologías con comas. El coach las usará para contextualizar su feedback.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 6: VENTANA DE MEMORIA DEL COACH */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-purple-600 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full font-bold text-sm">
                  6
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Ventana de Memoria del Coach
                </h4>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.Brain className="inline w-3 h-3 mr-1" />
                  Cuántos mensajes recientes debe analizar el coach al evaluar el desempeño
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Coach Memory Window (turnos)
                  </label>
                  <span className="text-2xl font-bold text-purple-600">
                    {agentData.memoryConfig?.coachMemoryWindow || 6}
                  </span>
                </div>
                
                <input
                  type="range"
                  min="2"
                  max="20"
                  value={agentData.memoryConfig?.coachMemoryWindow || 6}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setAgentData(prev => ({
                      ...prev,
                      memoryConfig: {
                        ...prev.memoryConfig,
                        coachMemoryWindow: newValue
                      }
                    }));
                  }}
                  disabled={mode === 'view'}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>2 turnos (mínimo)</span>
                  <span>20 turnos (máximo)</span>
                </div>

                <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-800">
                    <strong>💡 Recomendado: 6-8 turnos</strong>
                  </p>
                  <ul className="text-xs text-purple-700 mt-1 space-y-1 ml-3">
                    <li>• El coach analiza los últimos <strong>{agentData.memoryConfig?.coachMemoryWindow || 6} mensajes</strong></li>
                    <li>• Suficiente para detectar patrones recientes</li>
                    <li>• Menos contexto que IA Cliente (más enfocado)</li>
                  </ul>
                </div>

                {/* Advertencia si es muy alto */}
                {(agentData.memoryConfig?.coachMemoryWindow || 6) > 12 && (
                  <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <LucideIcons.AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        <strong>⚠️ Ventana muy amplia:</strong> Más de 12 turnos puede hacer que el coach pierda foco 
                        en patrones recientes. Considera usar 6-10 para evaluaciones más precisas.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* SECCIÓN 7: BRIEFING INICIAL (NARRADOR) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="border-t-4 border-emerald-600 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="flex items-center justify-center w-8 h-8 bg-emerald-600 text-white rounded-full font-bold text-sm">
                  7
                </span>
                <h4 className="text-base font-semibold text-gray-900">
                  Briefing Inicial (Narrador)
                </h4>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-gray-700">
                  <LucideIcons.Play className="inline w-3 h-3 mr-1" />
                  Brief narrativo que introduce al usuario al escenario <strong>antes</strong> de que IA_CLIENTE inicie la simulación
                </p>
              </div>

              {/* Enable/Disable Brief */}
              <div className="flex items-center gap-3 p-4 bg-emerald-100 border-2 border-emerald-300 rounded-lg mb-4">
                <input
                  type="checkbox"
                  id="briefingEnabled"
                  checked={agentData.coachConfig?.briefing?.enabled ?? true}
                  onChange={(e) => setAgentData({
                    ...agentData,
                    coachConfig: {
                      ...agentData.coachConfig,
                      briefing: {
                        ...agentData.coachConfig?.briefing,
                        enabled: e.target.checked
                      }
                    }
                  })}
                  disabled={mode === 'view'}
                  className="w-5 h-5 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <label htmlFor="briefingEnabled" className="text-sm font-medium text-gray-900 block">
                    Habilitar Brief Inicial (Narrador)
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    El coach generará un mensaje narrativo contextual <strong>antes</strong> de que el cliente IA inicie la conversación
                  </p>
                </div>
              </div>

              {/* Brief Configuration */}
              {agentData.coachConfig?.briefing?.enabled && (
                <div className="space-y-4">
                  {/* Meta-prompt del Brief */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta-prompt del Brief (Narrador) *
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        Define cómo el Coach introduce el escenario antes de que el Cliente IA inicie la simulación
                      </span>
                    </label>
                    <textarea
                      value={agentData.coachConfig?.briefing?.prompt || ''}
                      onChange={(e) => setAgentData({
                        ...agentData,
                        coachConfig: {
                          ...agentData.coachConfig,
                          briefing: {
                            ...agentData.coachConfig?.briefing,
                            prompt: e.target.value
                          }
                        }
                      })}
                      disabled={mode === 'view'}
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm disabled:bg-gray-100"
                      placeholder="Introduce al usuario en el escenario. Ejemplo:\n\nHoy vas a simular una conversación con un inversionista que está evaluando startups en una feria de innovación.\n\nFrente a ti está un inversionista con amplia experiencia en el sector tecnológico, dispuesto a escuchar tu propuesta. Este es tu momento de demostrar el valor de tu proyecto.\n\n¿Podrías contarme brevemente sobre tu startup y qué problema resuelve?"
                    />
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        <strong>💡 Características del brief:</strong>
                      </p>
                      <ul className="text-xs text-blue-700 mt-1 space-y-0.5 ml-3">
                        <li>• No es una pregunta directa del cliente, es contexto narrativo</li>
                        <li>• Prepara al usuario para la simulación (escenario, roles, objetivo)</li>
                        <li>• Mínimo 50 caracteres para evitar briefs vacíos</li>
                        <li>• Tono profesional pero amigable</li>
                      </ul>
                    </div>

                    {/* Validación */}
                    {agentData.coachConfig?.briefing?.prompt && agentData.coachConfig.briefing.prompt.trim().length < 50 && (
                      <div className="mt-2 bg-amber-50 border border-amber-300 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <LucideIcons.AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800">
                            <strong>⚠️ Brief muy corto:</strong> Se recomienda al menos 50 caracteres para un contexto útil
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RAG Toggle */}
                  <div className="p-4 bg-white border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={agentData.coachConfig?.briefing?.ragMode === 'on'}
                          onChange={(e) => setAgentData({
                            ...agentData,
                            coachConfig: {
                              ...agentData.coachConfig,
                              briefing: {
                                ...agentData.coachConfig?.briefing,
                                ragMode: e.target.checked ? 'on' : 'off'
                              }
                            }
                          })}
                          disabled={mode === 'view'}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-900">
                          RAG en Brief: {agentData.coachConfig?.briefing?.ragMode === 'on' ? 'ON' : 'OFF'}
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      Permite al coach consultar el corpus al generar el brief inicial
                    </p>

                    {/* Warning si RAG está ON */}
                    {agentData.coachConfig?.briefing?.ragMode === 'on' && (
                      <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <LucideIcons.AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-amber-900">
                              ⚠️ Advertencia: RAG en Brief
                            </p>
                            <p className="text-xs text-amber-800 mt-1">
                              Habilitar RAG en el brief puede afectar <strong>neutralidad y performance</strong>. 
                              El brief debe ser contextual y genérico. Usar RAG solo si es estrictamente necesario.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info adicional */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-xs text-slate-700">
                      <LucideIcons.Info className="inline w-3 h-3 mr-1" />
                      <strong>Nota:</strong> El brief hereda el provider (Gemini) y temperatura del Coach. 
                      No requiere configuración adicional de modelo.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Handlers para Competencias (FASE 1)
  const addCompetency = () => {
    setAgentData(prev => ({
      ...prev,
      evaluation: {
        ...prev.evaluation,
        competencies: [
          ...prev.evaluation.competencies,
          {
            competencyId: `comp-${Date.now()}`,
            displayName: '',
            description: '',
            weight: 20,
            criteria: []
          }
        ]
      }
    }));
  };

  const removeCompetency = (index) => {
    setAgentData(prev => ({
      ...prev,
      evaluation: {
        ...prev.evaluation,
        competencies: prev.evaluation.competencies.filter((_, i) => i !== index)
      }
    }));
  };

  const updateCompetency = (index, field, value) => {
    setAgentData(prev => {
      const newCompetencies = prev.evaluation.competencies.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      );
      return { 
        ...prev, 
        evaluation: { ...prev.evaluation, competencies: newCompetencies }
      };
    });
  };

  const addCriterion = (compIndex) => {
    setAgentData(prev => {
      const newCompetencies = prev.evaluation.competencies.map((comp, i) => {
        if (i === compIndex) {
          return {
            ...comp,
            criteria: [
              ...comp.criteria,
              {
                criterionId: `crit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                description: '',
                maxScore: 100
              }
            ]
          };
        }
        return comp;
      });
      return {
        ...prev,
        evaluation: { ...prev.evaluation, competencies: newCompetencies }
      };
    });
  };

  const removeCriterion = (compIndex, critIndex) => {
    setAgentData(prev => {
      const newCompetencies = prev.evaluation.competencies.map((comp, i) => {
        if (i === compIndex) {
          return {
            ...comp,
            criteria: comp.criteria.filter((_, ci) => ci !== critIndex)
          };
        }
        return comp;
      });
      return {
        ...prev,
        evaluation: { ...prev.evaluation, competencies: newCompetencies }
      };
    });
  };

  const updateCriterion = (compIndex, critIndex, field, value) => {
    setAgentData(prev => {
      const newCompetencies = prev.evaluation.competencies.map((comp, i) => {
        if (i === compIndex) {
          return {
            ...comp,
            criteria: comp.criteria.map((crit, ci) => 
              ci === critIndex ? { ...crit, [field]: value } : crit
            )
          };
        }
        return comp;
      });
      return {
        ...prev,
        evaluation: { ...prev.evaluation, competencies: newCompetencies }
      };
    });
  };

  const renderEvaluationTab = () => (
    <div className="space-y-6">
      {/* Header con Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Sistema de Evaluación Integral</h3>
          <p className="text-sm text-gray-600 mt-1">
            Evaluación pedagógica con análisis IA (Gemini 2.0 Flash Thinking)
          </p>
        </div>
        <button
          onClick={() => updateField('evaluation.enabled', !agentData.evaluation?.enabled)}
          disabled={mode === 'view'}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            agentData.evaluation?.enabled 
              ? 'bg-green-100 text-green-700' 
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {agentData.evaluation?.enabled ? 'Habilitado' : 'Deshabilitado'}
        </button>
      </div>

      {agentData.evaluation?.enabled && (
        <>
          {/* Configuración General */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center gap-2">
              <LucideIcons.Settings className="w-4 h-4" />
              Configuración General
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Turnos mínimos para evaluación
                </label>
                <input
                  type="number"
                  value={agentData.evaluation.generalConfig?.minimumTurnsForEvaluation || 3}
                  onChange={(e) => updateField('evaluation.generalConfig.minimumTurnsForEvaluation', parseInt(e.target.value))}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={agentData.evaluation.generalConfig?.includeStrengths !== false}
                    onChange={(e) => updateField('evaluation.generalConfig.includeStrengths', e.target.checked)}
                    disabled={mode === 'view'}
                    className="rounded"
                  />
                  Incluir fortalezas
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={agentData.evaluation.generalConfig?.includeWeaknesses !== false}
                    onChange={(e) => updateField('evaluation.generalConfig.includeWeaknesses', e.target.checked)}
                    disabled={mode === 'view'}
                    className="rounded"
                  />
                  Incluir debilidades
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={agentData.evaluation.generalConfig?.includeActionPlan !== false}
                    onChange={(e) => updateField('evaluation.generalConfig.includeActionPlan', e.target.checked)}
                    disabled={mode === 'view'}
                    className="rounded"
                  />
                  Incluir plan de acción
                </label>
              </div>
            </div>
          </div>

          {/* Labels Personalizables */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2 mb-4">
              <LucideIcons.Tag className="w-4 h-4" />
              Etiquetas Personalizables
            </h4>
            
            {/* Nota sobre clientRole movido */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-800">
                <LucideIcons.Info className="inline w-3 h-3 mr-1" />
                <strong>Nota:</strong> El "Rol del Cliente AI" se configura ahora en la pestaña 
                <strong> "IA Cliente" → Sección 1: Identidad</strong> para mantener toda la configuración del cliente en un solo lugar.
              </p>
            </div>

            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Rol del Participante
                </label>
                <input
                  type="text"
                  value={agentData.labels?.participantRole || 'PARTICIPANTE'}
                  onChange={(e) => updateField('labels.participantRole', e.target.value)}
                  disabled={mode === 'view'}
                  placeholder="Ej: PROJECT MANAGER, VENDEDOR, MÉDICO"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Cómo se identifica al usuario humano en la evaluación</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tipo de Actividad
                </label>
                <input
                  type="text"
                  value={agentData.labels?.activityType || 'simulación'}
                  onChange={(e) => updateField('labels.activityType', e.target.value)}
                  disabled={mode === 'view'}
                  placeholder="Ej: negociación, consulta médica, entrevista"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Descripción genérica del tipo de interacción</p>
              </div>
            </div>
          </div>

          {/* Competencias */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                <LucideIcons.Award className="w-4 h-4" />
                Competencias a Evaluar
              </h4>
              {mode !== 'view' && (
                <button 
                  onClick={addCompetency}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                  <LucideIcons.Plus className="w-4 h-4" />
                  Agregar Competencia
                </button>
              )}
            </div>

            {(agentData.evaluation?.competencies?.length || 0) === 0 ? (
              <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                <LucideIcons.Award className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm">No hay competencias configuradas</p>
                {mode !== 'view' && (
                  <button 
                    onClick={addCompetency}
                    className="mt-4 text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                    + Agregar primera competencia
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {(agentData.evaluation?.competencies || []).map((comp, compIndex) => (
                  <div key={`comp-${compIndex}-${comp.competencyId}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-medium text-gray-900">Competencia {compIndex + 1}</h5>
                      {mode !== 'view' && (
                        <button
                          onClick={() => removeCompetency(compIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <LucideIcons.Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      {/* Nombre y Peso */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Nombre de la Competencia
                          </label>
                          <input
                            type="text"
                            value={comp.displayName || ''}
                            onChange={(e) => updateCompetency(compIndex, 'displayName', e.target.value)}
                            disabled={mode === 'view'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Ej: Comunicación Asertiva"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Peso (%)
                          </label>
                          <input
                            type="number"
                            value={comp.weight || 20}
                            onChange={(e) => updateCompetency(compIndex, 'weight', parseFloat(e.target.value))}
                            disabled={mode === 'view'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            min="1"
                            max="100"
                          />
                        </div>
                      </div>

                      {/* Descripción */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Descripción
                        </label>
                        <textarea
                          value={comp.description || ''}
                          onChange={(e) => updateCompetency(compIndex, 'description', e.target.value)}
                          disabled={mode === 'view'}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          rows="2"
                          placeholder="Qué se evalúa en esta competencia..."
                        />
                      </div>

                      {/* Criterios */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs font-medium text-gray-700">
                            Criterios de Evaluación
                          </label>
                          {mode !== 'view' && (
                            <button
                              onClick={() => addCriterion(compIndex)}
                              className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                            >
                              <LucideIcons.Plus className="w-3 h-3" />
                              Agregar criterio
                            </button>
                          )}
                        </div>
                        
                        {(comp.criteria || []).length === 0 ? (
                          <div className="text-center py-6 text-gray-400 border border-dashed border-gray-300 rounded-lg text-xs">
                            No hay criterios. {mode !== 'view' && <button onClick={() => addCriterion(compIndex)} className="text-indigo-600 hover:underline">Agregar primero</button>}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {comp.criteria.map((crit, critIndex) => (
                              <div key={`crit-${compIndex}-${critIndex}-${crit.criterionId}`} className="flex gap-2 items-start border border-gray-200 rounded p-2">
                                <div className="flex-1">
                                  <textarea
                                    value={crit.description || ''}
                                    onChange={(e) => updateCriterion(compIndex, critIndex, 'description', e.target.value)}
                                    disabled={mode === 'view'}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    rows="2"
                                    placeholder="Descripción del criterio (texto libre analizable por Gemini)"
                                  />
                                </div>
                                <div className="w-20">
                                  <input
                                    type="number"
                                    value={crit.maxScore || 100}
                                    onChange={(e) => updateCriterion(compIndex, critIndex, 'maxScore', parseInt(e.target.value))}
                                    disabled={mode === 'view'}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                    placeholder="Max"
                                  />
                                </div>
                                {mode !== 'view' && (
                                  <button
                                    onClick={() => removeCriterion(compIndex, critIndex)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <LucideIcons.X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Configuración de Feedback */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-purple-900 mb-3 flex items-center gap-2">
              <LucideIcons.MessageSquare className="w-4 h-4" />
              Configuración de Feedback
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tono</label>
                <select
                  value={agentData.evaluation.feedbackSettings?.tone || 'motivacional'}
                  onChange={(e) => updateField('evaluation.feedbackSettings.tone', e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="formal">Formal</option>
                  <option value="amigable">Amigable</option>
                  <option value="motivacional">Motivacional</option>
                  <option value="directo">Directo</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nivel de Detalle</label>
                <select
                  value={agentData.evaluation.feedbackSettings?.detailLevel || 'detallado'}
                  onChange={(e) => updateField('evaluation.feedbackSettings.detailLevel', e.target.value)}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="conciso">Conciso</option>
                  <option value="detallado">Detallado</option>
                  <option value="exhaustivo">Exhaustivo</option>
                </select>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );



  const renderModeratorConfigTab = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <LucideIcons.ShieldAlert className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-orange-800 font-medium mb-1">
              Motor V3: Moderador Ligero de Comportamiento
            </p>
            <p className="text-xs text-orange-700">
              Supervisa el comportamiento del estudiante (spam, lenguaje ofensivo, abandono) usando OpenAI.
              Opera independientemente del Coach pedagógico para optimizar costos.
            </p>
          </div>
        </div>
      </div>

      {/* Enable Moderator */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={agentData.moderatorConfig?.enabled !== false}
          onChange={(e) => updateField('moderatorConfig.enabled', e.target.checked)}
          disabled={mode === 'view'}
          className="w-4 h-4 text-indigo-600 rounded"
        />
        <label className="text-sm font-medium text-gray-700">
          Habilitar Moderador de Comportamiento
        </label>
      </div>

      {agentData.moderatorConfig?.enabled !== false && (
        <>
          {/* Proveedor (fixed) y Modelo */}
          <div>
            <div className="flex items-center gap-4 mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Proveedor: <span className="text-blue-600 font-semibold">OpenAI</span>
              </label>
              <span className="text-xs text-gray-500">→</span>
              <label className="block text-sm font-medium text-gray-700">
                Modelo
              </label>
            </div>
            <select
              value={agentData.moderatorConfig?.model || 'gpt-4o-mini'}
              onChange={(e) => updateField('moderatorConfig.model', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="gpt-4o">gpt-4o (más preciso, $$$)</option>
              <option value="gpt-4o-mini">gpt-4o-mini (recomendado, $$)</option>
              <option value="gpt-4.1-nano">gpt-4.1-nano (económico, $)</option>
            </select>
          </div>

          {/* Temperature and Frequency */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Temperature
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  {agentData.moderatorConfig?.temperature || 0.1}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={agentData.moderatorConfig?.temperature || 0.1}
                onChange={(e) => updateField('moderatorConfig.temperature', parseFloat(e.target.value))}
                disabled={mode === 'view'}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: 0.1 (detección consistente)
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Frecuencia de Evaluación (cada N turnos)
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  {agentData.moderatorConfig?.evaluationFrequency || 2}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={agentData.moderatorConfig?.evaluationFrequency || 2}
                onChange={(e) => updateField('moderatorConfig.evaluationFrequency', parseInt(e.target.value))}
                disabled={mode === 'view'}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: 2 turnos (balance costo/protección)
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Ventana de Historial (mensajes)
                </label>
                <span className="text-sm font-semibold text-indigo-600">
                  {agentData.moderatorConfig?.historyWindow || 6}
                </span>
              </div>
              <input
                type="range"
                min="4"
                max="20"
                step="2"
                value={agentData.moderatorConfig?.historyWindow || 6}
                onChange={(e) => updateField('moderatorConfig.historyWindow', parseInt(e.target.value))}
                disabled={mode === 'view'}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: 10-12 (mejor detección de spam)
              </p>
            </div>
          </div>

          {/* Prompt Base */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt Base del Moderador
            </label>
            <textarea
              value={agentData.moderatorConfig?.promptBase || ''}
              onChange={(e) => updateField('moderatorConfig.promptBase', e.target.value)}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              rows="15"
              placeholder="Eres un moderador de comportamiento que supervisa conversaciones educativas..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Define COMPLETAMENTE cómo debe comportarse el moderador. No hay lógica hardcoded.
            </p>
          </div>

          {/* Custom Rules */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reglas Personalizadas
            </label>
            <div className="space-y-2">
              {(agentData.moderatorConfig?.customRules || []).map((rule, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={rule}
                    onChange={(e) => {
                      const newRules = [...(agentData.moderatorConfig?.customRules || [])];
                      newRules[idx] = e.target.value;
                      updateField('moderatorConfig.customRules', newRules);
                    }}
                    disabled={mode === 'view'}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Detectar menciones de violencia"
                  />
                  {mode !== 'view' && (
                    <button
                      onClick={() => {
                        const newRules = (agentData.moderatorConfig?.customRules || []).filter((_, i) => i !== idx);
                        updateField('moderatorConfig.customRules', newRules);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <LucideIcons.Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {mode !== 'view' && (
                <button
                  onClick={() => {
                    const newRules = [...(agentData.moderatorConfig?.customRules || []), ''];
                    updateField('moderatorConfig.customRules', newRules);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg"
                >
                  <LucideIcons.Plus className="w-4 h-4" />
                  Agregar Regla
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  const generateAgentFromWizard = async () => {
    try {
      setWizardGenLoading(true);
      setWizardGenError(null);
      setWizardGeneratedAgent(null);
      setWizardQa(null);
      setWizardQaRaw(null);
      setWizardGeneratorMeta(null);
      setWizardGenerationReport(null);
      setWizardPreviewOpen(false);
      setWizardGenState('generating');

      const normalizeQaPayload = (qa) => {
        const pceResult = qa?.pceResult;
        const wizardQaResult = qa?.wizardQaResult;
        const errors = Array.isArray(qa?.errors) ? qa.errors : [];
        const warnings = Array.isArray(qa?.warnings) ? qa.warnings : [];
        return { pceResult, wizardQaResult, errors, warnings };
      };

      const buildWizardReportFromQa = ({ errors, warnings }) => {
        const status = (errors.length > 0) ? 'BLOCK' : (warnings.length > 0 ? 'WARN' : 'PASS');
        return {
          summary: {
            status,
            counts: {
              P0: errors.length,
              P1: warnings.length,
              P2: 0
            }
          },
          checks: [...errors, ...warnings]
        };
      };

      const authToken = token || getSafeToken();
      if (!authToken) {
        throw new Error('No se encontró token de autenticación');
      }

      const payload = {
        definitionInputs: {
          generationPreset: 'wizard',
          agentName: (typeof wizardInputs.nombreVisible === 'string' && wizardInputs.nombreVisible.trim().length > 0)
            ? wizardInputs.nombreVisible.trim()
            : 'Agente',
          // canónico (contrato) + backward compat
          nombreVisible: (typeof wizardInputs.nombreVisible === 'string' && wizardInputs.nombreVisible.trim().length > 0)
            ? wizardInputs.nombreVisible.trim()
            : undefined,
          counterpartRole: wizardInputs.counterpartRole,
          participantRole: wizardInputs.userRole,
          userRole: wizardInputs.userRole,
          pedagogicalObjective: wizardInputs.pedagogicalObjective,
          simulationContext: wizardInputs.simulationContext,
          phaseCount: Number(wizardInputs.phaseCount),
          globalMaxTurns: Number(wizardInputs.maxTurns),
          maxTurns: Number(wizardInputs.maxTurns)
        }
      };

      builderDebugLog('[Wizard] generate payload (sanitized):', sanitizeBuildForLogs(payload));

      const genResponse = await fetch(`${VERTEX_API_BASE}/vertex/builder/generate-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      let genData;
      try {
        genData = await genResponse.json();
      } catch {
        throw new Error(`Error generando configuración (HTTP ${genResponse.status})`);
      }

      if (!genResponse.ok) {
        setWizardGeneratorMeta(genData?.generatorMeta || null);
        setWizardQaRaw(genData?.qa || null);

        const qaPayload = normalizeQaPayload(genData?.qa);
        if (qaPayload.errors.length > 0 || qaPayload.warnings.length > 0 || qaPayload.wizardQaResult || qaPayload.pceResult) {
          setWizardQa(qaPayload);
          setWizardGenerationReport(buildWizardReportFromQa(qaPayload));
        }

        const errorCode = genData?.errorCode;
        if (errorCode === 'GENERATOR_NOT_CONFIGURED') {
          throw new Error('Generador Wizard no configurado. Requiere BLUEPRINT_GEN_PROVIDER=openai, BLUEPRINT_GEN_MODEL y OPENAI_API_KEY.');
        }
        if (errorCode === 'LLM_CONFIG_MISSING_KEY') {
          throw new Error('Configuración LLM incompleta: falta OPENAI_API_KEY.');
        }

        throw new Error(errorCode || `Error generando configuración (HTTP ${genResponse.status})`);
      }

      const generated = genData?.agentConfig;
      if (!generated) {
        throw new Error('Respuesta inválida del generador: falta agentConfig/agent');
      }
      setWizardGeneratedAgent(generated);

      setWizardGenState('validating');

      const qa = normalizeQaPayload(genData?.qa);
      if (qa.pceResult !== 'PASS' && qa.pceResult !== 'FAIL') {
        throw new Error('Respuesta inválida del generador: qa.pceResult debe ser PASS | FAIL');
      }

      setWizardQa(qa);
      setWizardQaRaw(genData?.qa || null);
      setWizardGeneratorMeta(genData?.generatorMeta || null);

      const report = buildWizardReportFromQa(qa);

      setWizardGenerationReport(report);
      setBuildReport(report);

      const nextState = qa.pceResult === 'PASS' ? 'ready' : 'failed';
      setWizardGenState(nextState);
      if (nextState === 'ready') {
        setWizardPreviewOpen(true);
      }
    } catch (e) {
      setWizardGenError(e.message);
      setWizardGenState('failed');
    } finally {
      setWizardGenLoading(false);
    }
  };

  const applyWizardGeneratedAgent = async () => {
    const counts = wizardGenerationReport?.summary?.counts || {};
    const p0 = counts?.P0 || 0;
    if (p0 > 0 || wizardGenState !== 'ready' || wizardQa?.pceResult !== 'PASS') {
      setWizardGenError('No se puede aplicar: hay errores P0 en Build QA.');
      return;
    }
    if (!wizardGeneratedAgent) {
      setWizardGenError('No hay configuración generada para aplicar.');
      return;
    }
    if (!wizardGeneratorMeta?.blueprintHash) {
      setWizardGenError('No se puede aplicar: falta generatorMeta.blueprintHash.');
      return;
    }

    try {
      setWizardGenError(null);

      const authToken = token || getSafeToken();
      if (!authToken) {
        throw new Error('No se encontró token de autenticación');
      }

      const qaToSend = (wizardQaRaw && typeof wizardQaRaw === 'object')
        ? wizardQaRaw
        : {
          status: 'ok',
          pceResult: wizardQa?.pceResult,
          wizardQaResult: wizardQa?.wizardQaResult,
          errors: Array.isArray(wizardQa?.errors) ? wizardQa.errors : [],
          warnings: Array.isArray(wizardQa?.warnings) ? wizardQa.warnings : []
        };

      const applyResponse = await fetch(`${VERTEX_API_BASE}/wizard/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          schemaVersion: 'agent-config.v4',
          agentConfig: wizardGeneratedAgent,
          generatorMeta: wizardGeneratorMeta,
          qa: qaToSend
        })
      });

      let applyData;
      try {
        applyData = await applyResponse.json();
      } catch {
        throw new Error(`Error aplicando Wizard (HTTP ${applyResponse.status})`);
      }

      if (!applyResponse.ok) {
        const code = applyData?.errorCode || `HTTP_${applyResponse.status}`;
        const errs = Array.isArray(applyData?.qa?.errors) ? applyData.qa.errors : [];
        const detail = errs.length > 0
          ? ` (${errs[0]?.checkId || 'ERROR'}: ${errs[0]?.message || 'error'})`
          : '';
        throw new Error(`${code}${detail}`);
      }

      const savedAgentId = applyData?.agentId;
      if (!savedAgentId) {
        throw new Error('Respuesta inválida: falta agentId');
      }

      setSuccessMessage('Agente guardado desde Wizard. Abriendo editor...');
      navigate(`/vertex/builder/edit/${savedAgentId}`);
    } catch (e) {
      setWizardGenError(e.message);
    }
  };

  const togglePartialScope = (scopeKey) => {
    setPartialRegenPatchScope((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(scopeKey)) return current.filter((s) => s !== scopeKey);
      return [...current, scopeKey];
    });

    // Si cambia el scope, el preview anterior ya no es aplicable.
    setPartialRegenPreview(null);
    setPartialRegenError(null);
    setPartialRegenApplyError(null);
  };

  const regeneratePartialPreview = async () => {
    if (!agentData?.agentId) {
      setPartialRegenError('No se puede regenerar: falta agentId (agente no guardado).');
      return;
    }
    if (!Array.isArray(partialRegenPatchScope) || partialRegenPatchScope.length === 0) {
      setPartialRegenError('Selecciona al menos un bloque para regenerar.');
      return;
    }

    try {
      setPartialRegenError(null);
      setPartialRegenApplyError(null);
      setPartialRegenLoading(true);

      const authToken = token || getSafeToken();
      if (!authToken) throw new Error('No se encontró token de autenticación');

      const resp = await fetch(`${VERTEX_API_BASE}/wizard/regenerate-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          schemaVersion: 'agent-config.v4',
          agentConfig: agentData,
          patchScope: partialRegenPatchScope
        })
      });

      let data;
      try {
        data = await resp.json();
      } catch {
        throw new Error(`Error regenerando preview (HTTP ${resp.status})`);
      }

      if (!resp.ok) {
        const code = data?.errorCode || `HTTP_${resp.status}`;
        const errs = Array.isArray(data?.qa?.errors) ? data.qa.errors : [];
        const detail = errs.length > 0
          ? ` (${errs[0]?.checkId || 'ERROR'}: ${errs[0]?.message || 'error'})`
          : '';
        throw new Error(`${code}${detail}`);
      }

      setPartialRegenPreview(data);
    } catch (e) {
      setPartialRegenPreview(null);
      setPartialRegenError(e.message);
    } finally {
      setPartialRegenLoading(false);
    }
  };

  const applyPartialRegenerate = async () => {
    if (!agentData?.agentId) {
      setPartialRegenApplyError('No se puede aplicar: falta agentId (agente no guardado).');
      return;
    }

    const previewQa = partialRegenPreview?.qa;
    const previewMeta = partialRegenPreview?.generatorMeta;
    const previewAgentConfig = partialRegenPreview?.agentConfig;

    const previewScope = Array.isArray(previewMeta?.patchScope) ? previewMeta.patchScope : [];
    const scopeMatches = Array.isArray(partialRegenPatchScope)
      && previewScope.length === partialRegenPatchScope.length
      && previewScope.every((s) => partialRegenPatchScope.includes(s));

    const qaOk = previewQa?.status === 'ok' && Array.isArray(previewQa?.errors) && previewQa.errors.length === 0;
    if (!qaOk) {
      setPartialRegenApplyError('Apply deshabilitado: QA no está OK.');
      return;
    }
    if (!scopeMatches) {
      setPartialRegenApplyError('Apply deshabilitado: patchScope no coincide con el preview.');
      return;
    }
    if (!previewMeta || previewMeta.mode !== 'preview') {
      setPartialRegenApplyError('No se puede aplicar: falta generatorMeta válido (modo preview).');
      return;
    }
    if (!previewAgentConfig) {
      setPartialRegenApplyError('No se puede aplicar: falta agentConfig preview.');
      return;
    }

    try {
      setPartialRegenApplyError(null);
      setPartialRegenApplyLoading(true);

      const authToken = token || getSafeToken();
      if (!authToken) throw new Error('No se encontró token de autenticación');

      const resp = await fetch(`${VERTEX_API_BASE}/wizard/regenerate-apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          schemaVersion: 'agent-config.v4',
          agentId: agentData.agentId,
          agentConfig: previewAgentConfig,
          patchScope: previewScope,
          qa: previewQa,
          generatorMeta: previewMeta
        })
      });

      let data;
      try {
        data = await resp.json();
      } catch {
        throw new Error(`Error aplicando cambios (HTTP ${resp.status})`);
      }

      if (!resp.ok) {
        const code = data?.errorCode || `HTTP_${resp.status}`;
        const errs = Array.isArray(data?.qa?.errors) ? data.qa.errors : [];
        const detail = errs.length > 0
          ? ` (${errs[0]?.checkId || 'ERROR'}: ${errs[0]?.message || 'error'})`
          : '';
        throw new Error(`${code}${detail}`);
      }

      setSuccessMessage('Cambios aplicados. Recargando agente...');
      setPartialRegenPreview(null);
      await fetchAgentData();
    } catch (e) {
      setPartialRegenApplyError(e.message);
    } finally {
      setPartialRegenApplyLoading(false);
    }
  };

  const renderAIWizardTab = () => {
    const status = wizardGenerationReport?.summary?.status || null;
    const counts = wizardGenerationReport?.summary?.counts || null;
    const p0 = counts?.P0 || 0;
    const canApply = !!wizardGeneratedAgent
      && wizardGenState === 'ready'
      && wizardQa?.pceResult === 'PASS'
      && (wizardQa?.wizardQaResult == null || wizardQa.wizardQaResult === 'PASS' || wizardQa.wizardQaResult === 'WARN')
      && p0 === 0;
    const showBlueprintPanel = !!wizardGeneratedAgent && wizardGenState === 'ready' && (status === 'PASS' || status === 'WARN');

    const requiredOk =
      !isEmptyValue(wizardInputs.counterpartRole)
      && !isEmptyValue(wizardInputs.userRole)
      && !isEmptyValue(wizardInputs.pedagogicalObjective)
      && !isEmptyValue(wizardInputs.simulationContext)
      && [1, 2, 3, 4, 5, 6].includes(Number(wizardInputs.phaseCount));

    const canGenerate = requiredOk
      && !wizardGenLoading
      && wizardGenState !== 'generating'
      && wizardGenState !== 'validating'
      && mode !== 'view';

    const statusStyles = {
      PASS: 'bg-green-100 text-green-800 border-green-200',
      WARN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      BLOCK: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Generación asistida (Wizard)</h2>
          <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-700">Genera una configuración completa desde inputs mínimos.</p>
            <p className="text-sm text-gray-700">La salida es solo preview hasta que apliques o guardes.</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900">Regeneración parcial (Preview → Apply)</h3>
          <p className="mt-1 text-sm text-gray-700">Regenera bloques específicos del agente actual y aplica solo si la QA queda OK.</p>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
                checked={Array.isArray(partialRegenPatchScope) && partialRegenPatchScope.includes('onboarding')}
                onChange={() => togglePartialScope('onboarding')}
                disabled={mode === 'view' || partialRegenLoading || partialRegenApplyLoading}
              />
              Onboarding
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
                checked={Array.isArray(partialRegenPatchScope) && partialRegenPatchScope.includes('stateMachine')}
                onChange={() => togglePartialScope('stateMachine')}
                disabled={mode === 'view' || partialRegenLoading || partialRegenApplyLoading}
              />
              Máquina de estados
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
                checked={Array.isArray(partialRegenPatchScope) && partialRegenPatchScope.includes('coachConfig')}
                onChange={() => togglePartialScope('coachConfig')}
                disabled={mode === 'view' || partialRegenLoading || partialRegenApplyLoading}
              />
              Coach
            </label>
          </div>

          {!agentData?.agentId && (
            <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-900">
              Para regeneración parcial necesitas un agente ya guardado (agentId).
            </div>
          )}

          {(partialRegenError || partialRegenApplyError) && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900">
              {partialRegenError || partialRegenApplyError}
            </div>
          )}

          {partialRegenPreview?.qa && (
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-800">
                  QA: <span className="font-semibold">{String(partialRegenPreview.qa.status || '').toUpperCase()}</span>
                </p>
                <p className="text-xs text-gray-600">
                  Errors: {Array.isArray(partialRegenPreview.qa.errors) ? partialRegenPreview.qa.errors.length : 0} · Warnings: {Array.isArray(partialRegenPreview.qa.warnings) ? partialRegenPreview.qa.warnings.length : 0}
                </p>
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={regeneratePartialPreview}
              disabled={mode === 'view' || partialRegenLoading || partialRegenApplyLoading || !agentData?.agentId}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {partialRegenLoading ? 'Regenerando…' : 'Regenerate preview'}
            </button>

            <button
              type="button"
              onClick={applyPartialRegenerate}
              disabled={
                mode === 'view'
                || partialRegenLoading
                || partialRegenApplyLoading
                || !agentData?.agentId
                || !(partialRegenPreview?.qa?.status === 'ok' && Array.isArray(partialRegenPreview?.qa?.errors) && partialRegenPreview.qa.errors.length === 0)
                || !(
                  Array.isArray(partialRegenPreview?.generatorMeta?.patchScope)
                  && Array.isArray(partialRegenPatchScope)
                  && partialRegenPreview.generatorMeta.patchScope.length === partialRegenPatchScope.length
                  && partialRegenPreview.generatorMeta.patchScope.every((s) => partialRegenPatchScope.includes(s))
                )
              }
              className="px-3 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {partialRegenApplyLoading ? 'Aplicando…' : 'Apply changes'}
            </button>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre visible (opcional)</label>
            <input
              type="text"
              value={wizardInputs.nombreVisible}
              onChange={(e) => setWizardInputs((prev) => ({ ...prev, nombreVisible: e.target.value }))}
              disabled={mode === 'view'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="Ej: Simulador de Negociación — Cliente Exigente"
            />
            <p className="mt-1 text-xs text-gray-500">Si se deja vacío, se usará: Agente</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Intención</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol que simula el Agente (contraparte)</label>
                <input
                  type="text"
                  value={wizardInputs.counterpartRole}
                  onChange={(e) => setWizardInputs((prev) => ({ ...prev, counterpartRole: e.target.value }))}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Director(a) Comercial"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol del usuario (userRole)</label>
                <input
                  type="text"
                  value={wizardInputs.userRole}
                  onChange={(e) => setWizardInputs((prev) => ({ ...prev, userRole: e.target.value }))}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: KAM responsable del contrato"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Objetivo pedagógico</label>
              <textarea
                value={wizardInputs.pedagogicalObjective}
                onChange={(e) => setWizardInputs((prev) => ({ ...prev, pedagogicalObjective: e.target.value }))}
                disabled={mode === 'view'}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Ej: Practicar concesiones y cierres con criterios objetivos"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contexto de la simulación</label>
              <textarea
                value={wizardInputs.simulationContext}
                onChange={(e) => setWizardInputs((prev) => ({ ...prev, simulationContext: e.target.value }))}
                disabled={mode === 'view'}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                placeholder="Describe brevemente el escenario, restricciones, tensiones y qué está en juego."
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Límites del ejercicio</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad de fases del diálogo</label>
                <select
                  value={wizardInputs.phaseCount}
                  onChange={(e) => setWizardInputs((prev) => ({ ...prev, phaseCount: Number(e.target.value) }))}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                  <option value={6}>6</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Por defecto, el Wizard ajusta maxTurns según las fases.</p>
              </div>
              <div />
            </div>
          </div>

          <details className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <summary className="cursor-pointer text-sm font-semibold text-gray-900">Avanzado</summary>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Turnos máximos (maxTurns)</label>
                <input
                  type="number"
                  min={1}
                  value={wizardInputs.maxTurns}
                  onChange={(e) => setWizardInputs((prev) => ({ ...prev, maxTurns: Number(e.target.value) }))}
                  disabled={mode === 'view'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div />
            </div>
          </details>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={generateAgentFromWizard}
              disabled={!canGenerate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {wizardGenState === 'generating' ? (
                <>
                  <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                  Generando con Wizard...
                </>
              ) : wizardGenState === 'validating' ? (
                <>
                  <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                  Validando configuración (Build QA)...
                </>
              ) : (
                <>
                  <LucideIcons.Sparkles className="w-4 h-4" />
                  Generar configuración
                </>
              )}
            </button>

            <button
              onClick={applyWizardGeneratedAgent}
              disabled={!canApply || mode === 'view'}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <LucideIcons.Check className="w-4 h-4" />
              Aplicar al editor
            </button>
          </div>
        </div>

        {wizardGenError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <LucideIcons.AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm text-red-700">{wizardGenError}</p>
              {wizardQa?.wizardQaResult && (
                <p className="text-xs text-red-700 mt-1">wizardQaResult: {wizardQa.wizardQaResult}</p>
              )}
            </div>
          </div>
        )}

        {wizardGenerationReport && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Estado:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusStyles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                  {status}
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <span className="text-red-700 font-medium">P0: {counts?.P0 ?? 0}</span>
                <span className="text-yellow-700 font-medium">P1: {counts?.P1 ?? 0}</span>
                <span className="text-gray-700 font-medium">P2: {counts?.P2 ?? 0}</span>
              </div>
            </div>

            {p0 > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-semibold">Build bloqueado por P0. Corregir antes de aplicar/guardar.</p>
              </div>
            )}

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-900">Issues</h3>
              {Array.isArray(wizardGenerationReport.checks) && wizardGenerationReport.checks.length > 0 ? (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {wizardGenerationReport.checks.map((c, idx) => {
                    const sev = c.severity;
                    const sevStyle = sev === 'P0'
                      ? 'bg-red-100 text-red-800'
                      : sev === 'P1'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800';

                    return (
                      <div key={idx} className="p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${sevStyle}`}>{sev}</span>
                            <span className="text-xs text-gray-500 font-mono break-all">{c.componentPath}</span>
                            <span className="text-xs text-gray-400">{c.checkId}</span>
                          </div>
                          <p className="text-sm text-gray-900 mt-1">{c.message}</p>
                          <p className="text-xs text-gray-600 mt-1">Fix: {c.fixHint}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-600">No hay issues.</p>
              )}
            </div>

            {wizardGeneratorMeta && (
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <p className="text-xs text-gray-600">generatorMeta</p>
                <pre className="mt-2 text-xs text-gray-800 overflow-auto max-h-48 whitespace-pre-wrap">{JSON.stringify(wizardGeneratorMeta, null, 2)}</pre>
              </div>
            )}
          </div>
        )}

        {showBlueprintPanel && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="text-sm font-semibold text-gray-900">Blueprint del Agente (solo lectura)</h3>
              <button
                type="button"
                onClick={() => setWizardPreviewOpen((prev) => !prev)}
                className="text-sm text-indigo-700 hover:text-indigo-900"
              >
                {wizardPreviewOpen ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>

            {wizardPreviewOpen && (
              <details className="border border-gray-200 rounded-lg p-3 bg-gray-50" open>
                <summary className="cursor-pointer text-sm text-gray-800">agentConfig</summary>
                <pre className="mt-3 text-xs text-gray-800 overflow-auto max-h-96 whitespace-pre-wrap">
                  {JSON.stringify(wizardGeneratedAgent, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  const validateBuildQa = async () => {
    const realAgentId = agentData.agentId || agentId;
    if (!realAgentId) {
      setBuildQaError('Debe guardar el agente antes de validar');
      return;
    }

    try {
      setBuildQaLoading(true);
      setBuildQaError(null);

      const authToken = token || getSafeToken();
      if (!authToken) {
        throw new Error('No se encontró token de autenticación');
      }

      const buildSnapshot = { ...agentData };

      const response = await fetch(`${VERTEX_API_BASE}/v4/agents/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ agentId: realAgentId, buildSnapshot })
      });

      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || data.message || 'Error validando build');
      }

      setBuildReport(data.buildReport);
      builderDebugLog('[Build QA] buildReport (sanitized):', sanitizeBuildForLogs(data.buildReport));
    } catch (e) {
      setBuildQaError(e.message);
    } finally {
      setBuildQaLoading(false);
    }
  };

  const renderBuildQATab = () => {
    const status = buildReport?.summary?.status || null;
    const counts = buildReport?.summary?.counts || null;
    const isBlocked = (counts?.P0 || 0) > 0;

    const statusStyles = {
      PASS: 'bg-green-100 text-green-800 border-green-200',
      WARN: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      BLOCK: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Build QA</h2>
            <p className="text-sm text-gray-600">Valida coherencia del build y bloquea publicación si hay P0.</p>
          </div>

          <button
            onClick={validateBuildQa}
            disabled={buildQaLoading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {buildQaLoading ? (
              <>
                <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                Validando...
              </>
            ) : (
              <>
                <LucideIcons.CheckCircle className="w-4 h-4" />
                Validar coherencia del Build
              </>
            )}
          </button>
        </div>

        {buildQaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <LucideIcons.AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{buildQaError}</p>
          </div>
        )}

        {buildReport && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Estado:</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusStyles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                  {status}
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <span className="text-red-700 font-medium">P0: {counts?.P0 ?? 0}</span>
                <span className="text-yellow-700 font-medium">P1: {counts?.P1 ?? 0}</span>
                <span className="text-gray-700 font-medium">P2: {counts?.P2 ?? 0}</span>
              </div>
            </div>

            {isBlocked && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-semibold">Build bloqueado por P0. Corregir antes de publicar.</p>
              </div>
            )}

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Validaciones Realizadas</h3>
              
              {buildReport && typeof buildReport === 'object' ? (
                <div className="space-y-3">
                  {/* Si hay checks con issues */}
                  {Array.isArray(buildReport.checks) && buildReport.checks.length > 0 ? (
                    <>
                      {/* Issues Detectados */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <LucideIcons.AlertTriangle className="w-4 h-4 text-red-600" />
                          <h4 className="text-sm font-semibold text-red-900">Issues Detectados</h4>
                        </div>
                        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                          {buildReport.checks.map((c, idx) => {
                            const sev = c.severity;
                            const sevStyle = sev === 'P0'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : sev === 'P1'
                                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                : 'bg-gray-100 text-gray-800 border-gray-200';

                            return (
                              <div key={`issue-${idx}`} className="p-3 hover:bg-gray-50 transition-colors">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-2">
                                    <span className={`px-2 py-0.5 text-xs font-semibold rounded border ${sevStyle}`}>{sev}</span>
                                    <span className="text-xs text-gray-500 font-mono break-all">{c.componentPath || 'general'}</span>
                                    {c.checkId && <span className="text-xs text-gray-400">{c.checkId}</span>}
                                  </div>
                                  <p className="text-sm text-gray-900 mb-1">{c.message}</p>
                                  {c.fixHint && <p className="text-xs text-gray-600">💡 Fix: {c.fixHint}</p>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Estado PASS - Sin issues */
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-start gap-3">
                        <LucideIcons.CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-green-900 mb-2">
                            ✅ Todas las validaciones pasaron exitosamente
                          </h4>
                          <p className="text-xs text-green-700 mb-3">
                            El Build QA ha verificado la coherencia y no encontró problemas.
                          </p>
                          
                          {/* Categorías validadas */}
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-green-800 mb-2">Categorías validadas:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>Prompts obligatorios completos</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>State Machine (fases, turnos, transiciones)</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>Configuración de escenarios</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>Criterios de evaluación</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>RAG y recursos corporales</span>
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-green-700">
                                <LucideIcons.Check className="w-3 h-3" />
                                <span>Longitud de prompts (P2)</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resumen final */}
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className="font-medium">
                        Última validación: {buildReport.timestamp ? new Date(buildReport.timestamp).toLocaleString('es-ES', { 
                          dateStyle: 'short', 
                          timeStyle: 'short' 
                        }) : 'N/A'}
                      </span>
                      <div className="flex items-center gap-3">
                        {Array.isArray(buildReport.checks) && buildReport.checks.length === 0 ? (
                          <span className="text-green-700 font-medium">
                            ✓ Sin issues detectados
                          </span>
                        ) : (
                          <span className="text-red-700 font-medium">
                            ✗ {buildReport.checks?.length || 0} issues encontrados
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* No hay buildReport */
                <div className="text-center py-6 text-gray-500">
                  <LucideIcons.Info className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No hay validaciones disponibles.</p>
                  <p className="text-xs text-gray-400 mt-1">Presiona "Validar coherencia del Build" para ejecutar checks.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralTab();
      case 'onboarding':
        return renderOnboardingTab();
      case 'aiWizard':
        return renderAIWizardTab();
      case 'stateMachine':
        return renderStateMachineTab();
      case 'scenarios':
        return renderScenariosTab();
      case 'clientConfig':
        return renderClientConfigTab();
      case 'coachConfig':
        return renderCoachConfigTab();
      case 'moderatorConfig':
        return renderModeratorConfigTab();
      case 'evaluation':
        return renderEvaluationTab();
      case 'buildQA':
        return renderBuildQATab();
      case 'promptComposer':
        return <PromptComposerTab />;
      default:
        return null;
    }
  };

  const isPublishBlocked = (buildReport?.summary?.counts?.P0 || 0) > 0;

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LucideIcons.Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando agente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/vertex/builder')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <LucideIcons.ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    {mode === 'new' ? 'Nuevo Agente' : mode === 'view' ? 'Ver Agente' : 'Editar Agente'}
                  </h1>
                  {/* Badge de estado - Solo si no es modo nuevo */}
                  {mode !== 'new' && agentData.status && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      agentData.status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {agentData.status === 'published' ? 'Publicado' : 'Borrador'}
                    </span>
                  )}
                </div>
                {agentData.nombreVisible && (
                  <p className="text-sm text-gray-600 mt-1">
                    {agentData.nombreVisible} <span className="text-gray-400">({agentData.agentId})</span>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Botón Publicar/Despublicar - Solo si ya existe el agente */}
              {mode !== 'view' && mode !== 'new' && agentId && (
                <button
                  onClick={handlePublish}
                  disabled={saving || isPublishBlocked}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    agentData.status === 'published' 
                      ? 'bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-800 border border-green-200 hover:bg-green-200'
                  }`}
                >
                  {saving ? (
                    <>
                      <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                      {agentData.status === 'published' ? 'Despublicando...' : 'Publicando...'}
                    </>
                  ) : (
                    <>
                      {agentData.status === 'published' ? (
                        <>
                          <LucideIcons.EyeOff className="w-4 h-4" />
                          Despublicar
                        </>
                      ) : (
                        <>
                          <LucideIcons.Eye className="w-4 h-4" />
                          Publicar
                        </>
                      )}
                    </>
                  )}
                </button>
              )}

              {/* Botón Guardar */}
              {mode !== 'view' && (
                <button
                  onClick={handleSave}
                  disabled={
                    saving ||
                    (buildReport && (buildReport?.summary?.counts?.P0 || 0) > 0) ||
                    (mode === 'new' && (wizardGenState !== 'ready' || wizardQa?.pceResult !== 'PASS' || !wizardGeneratedAgent))
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <LucideIcons.Loader2 className="w-4 h-4 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <LucideIcons.Save className="w-4 h-4" />
                      Guardar Cambios
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <LucideIcons.AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {isPublishBlocked && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <LucideIcons.ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-semibold">Build bloqueado por P0. Corregir antes de publicar.</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <LucideIcons.CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Selector inicial (solo Nuevo Agente) */}
      {showInitialModeSelector && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">¿Qué quieres hacer?</h2>
                <p className="text-xs text-gray-600 mt-1">Recomendado: genera un Prompt Pack y copia manualmente.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCreationMode('composer')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <LucideIcons.PenTool className="w-4 h-4" />
                  Generar Prompt Pack (Composer)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCreationMode('manual');
                    setActiveTab('general');
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <LucideIcons.Settings className="w-4 h-4" />
                  Crear agente completo (Manual Builder)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modo Composer (solo Nuevo Agente) */}
      {isComposerMode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Modo: Composer</h2>
                <p className="text-xs text-gray-600 mt-1">Genera un Prompt Pack y luego vuelve para copiarlo al builder manual.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/vertex/builder/composer')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <LucideIcons.PenTool className="w-4 h-4" />
                  Abrir Composer (Prompt Pack)
                </button>
                <button
                  type="button"
                  onClick={() => setCreationMode(null)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <LucideIcons.ArrowLeft className="w-4 h-4" />
                  Cambiar modo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {isManualMode && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {isNewAgent && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Modo: Manual Builder</h2>
                  <p className="text-xs text-gray-600 mt-1">Configura el agente completo desde el builder.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setCreationMode(null)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <LucideIcons.ArrowLeft className="w-4 h-4" />
                  Cambiar modo
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto">
                {VISIBLE_TABS.map(tab => {
                  const Icon = LucideIcons[tab.icon];
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-3 border-b-2 whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ayuda - Arquitectura del Prompt */}
      {showArchitectureHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header del Modal */}
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LucideIcons.Layers className="w-6 h-6 text-white" />
                <h3 className="text-lg font-bold text-white">
                  Arquitectura del Prompt: Cómo funciona el IA Cliente
                </h3>
              </div>
              <button
                onClick={() => setShowArchitectureHelp(false)}
                className="text-white hover:bg-slate-700 rounded-lg p-2 transition-colors"
              >
                <LucideIcons.X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Introducción */}
              <div className="bg-slate-50 border border-slate-300 rounded-lg p-4">
                <p className="text-sm text-slate-700">
                  El comportamiento del Cliente IA se construye <strong>apilando</strong> diferentes capas de prompts.
                  Cada pestaña del builder contribuye una capa específica. Así es como se combinan:
                </p>
              </div>

              {/* Los 4 Bloques */}
              <div className="space-y-4">
                {/* Bloque 1 */}
                <div className="border-l-4 border-indigo-600 bg-indigo-50 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      1
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 mb-1">IDENTIDAD BASE</div>
                      <div className="text-sm text-gray-600 mb-2">
                        📍 Pestaña <strong>"IA Cliente"</strong> → Campo "Role Definition"
                      </div>
                      <div className="bg-white border border-indigo-300 rounded p-3 text-sm">
                        <div className="font-semibold text-indigo-900 mb-1">Quién es siempre:</div>
                        <div className="text-gray-700 italic">
                          "Eres Laura Martínez, CFO con 15 años de experiencia. Tu personalidad es analítica y directa..."
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-indigo-800">
                        💡 Define la identidad <strong>permanente</strong> del personaje
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bloque 2 */}
                <div className="border-l-4 border-amber-600 bg-amber-50 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      2
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 mb-1">SITUACIÓN ESPECÍFICA</div>
                      <div className="text-sm text-gray-600 mb-2">
                        📍 Pestaña <strong>"Escenarios"</strong> → Sección 2 (Contexto) y Sección 3 (Objetivo)
                      </div>
                      <div className="bg-white border border-amber-300 rounded p-3 text-sm">
                        <div className="font-semibold text-amber-900 mb-1">Qué pasa ahora:</div>
                        <div className="text-gray-700 italic">
                          "El proveedor incumplió el SLA 3 veces. Buscas compensación económica y garantías técnicas..."
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-amber-800">
                        💡 Define el <strong>problema actual</strong> que motiva este encuentro
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bloque 3 */}
                <div className="border-l-4 border-green-600 bg-green-50 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      3
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 mb-1">FASE ACTUAL</div>
                      <div className="text-sm text-gray-600 mb-2">
                        📍 Pestaña <strong>"State Machine"</strong> → Phase Prompt de la fase activa
                      </div>
                      <div className="bg-white border border-green-300 rounded p-3 text-sm">
                        <div className="font-semibold text-green-900 mb-1">Cómo actuar:</div>
                        <div className="text-gray-700 italic">
                          "FASE: OPENING - Muestra frustración inicial. Exige plan de acción inmediato..."
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-green-800">
                        💡 Define las <strong>tácticas</strong> específicas de la fase actual
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bloque 4 */}
                <div className="border-l-4 border-red-600 bg-red-50 rounded-r-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      4
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 mb-1">NIVEL DE PRESIÓN</div>
                      <div className="text-sm text-gray-600 mb-2">
                        📍 Pestaña <strong>"Escenarios"</strong> → Sección 4 (Estado Emocional)
                      </div>
                      <div className="bg-white border border-red-300 rounded p-3 text-sm">
                        <div className="font-semibold text-red-900 mb-1">Intensidad:</div>
                        <div className="text-gray-700 italic">
                          "Estás frustrado porque el proveedor incumplió 3 veces. Sé escéptico y exigente..."
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-red-800">
                        💡 Define <strong>qué tan difícil</strong> será el cliente
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guías de Uso */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <LucideIcons.CheckCircle className="w-5 h-5 text-green-600" />
                    <div className="font-bold text-green-900 text-sm">Estrategia correcta</div>
                  </div>
                  <ul className="space-y-2 text-sm text-green-800">
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span><strong>Identidad</strong> en IA Cliente (quién es siempre)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span><strong>Problema</strong> en Escenarios (situación actual)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span><strong>Táctica</strong> en State Machine (cómo actuar)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <LucideIcons.AlertCircle className="w-5 h-5 text-red-600" />
                    <div className="font-bold text-red-900 text-sm">Evita duplicar</div>
                  </div>
                  <ul className="space-y-2 text-sm text-red-800">
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>NO repitas la identidad del personaje en Escenarios</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>NO pongas tácticas en Escenarios</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <span>Cada capa tiene un propósito único</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setShowArchitectureHelp(false)}
                className="w-full px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors font-medium"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentEditor;
